import Util from './util';
import Translate from './translate';

import * as log from 'loglevel';
import ShopifyStore from './shopify-store';
import {render} from 'lit-html';

import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';

import './style/style.css';
import './style/options-variables.css'; // NEW: themes based css variables
//import './style/options.less'; // TODO remove after css render added

import productOptionsTemplate from './template/product-options.template';
import productOptionsPopupTemplate from './template/product-options-popup.template';
import ProductPreview from './product-preview';
import ConditionsEngine from './conditions-engine';
import Upload from './upload/upload';
import ShopifyCart from './shopify-cart';
import {ThemeConfig, CartItemProperties, ThemeCurrency, PriceType, AppExtensions, currencyConvert, PREVIEW_STATE} from './const';
import {formatMoney} from './money';
import CartOptionsPrice from './cart-options-price';
import ProductVariantOptions from './product-variant-options';
import ResourceLoader from './resource-loader';
import ProductGallery from './product-gallery';
import ConfigTranslate from './config-translate';
import LibraryController from './options/library.option';
import AiController from './options/ai.option';
import SavedConfigs, {numbered} from './config/saved-configs';
import Order from './order/order';
import {exportConfigUrl} from './export-url';
import {parseTextEditorProperty} from './template/template-util.js';

class ProductOptions {
    constructor(config, options) {
        // Default settings
        let defaults = {
            api: 'https://api.cloudlift.app',
            assets: 'https://assets.cloudlift.app',
            queryParams: {},
            selector: '.cl-options',
            position: '', // optional element position used with https://developer.mozilla.org/en-US/docs/Web/API/Element/insertAdjacentElement
            selectorPreview: '.cl-preview',
            selectorThumbnail: '', // optional selector for theme gallery thumbnails - passed to ProductPreview
            positionPreview: '',
            stickyPreview: false,
            stickyPreviewMobile: 'overlay',
            stickyPreviewMobileSize: 25,
            previewZoom: null, // optional zoom config for preview
            maxHeightPreview: 0,
            selectorForm: 'form[action*="/cart/add"]', // overwritten in app backend
            classNamePrefix: 'cl-po',
            product: {},
            priceDraft: true, // flag to use draft orders instead of hidden cart item
            priceWeight: false, // flag to calculate weight of options
            sku: ThemeConfig.productSKU, // flag to calculate sku of options (simpler mapping on themeConfig than from global app config)
            skuProp: window.clSKUProp || 'SKU', // could be made configurable
            skuSep: typeof window.clSKUSep == 'string' ? window.clSKUSep : '-', // could be made configurable
            errorPrefix: 'product.options.validity.',
            editorMode: false,
            visible: {}, // current visible option names
            visibleEditor: {}, // visible overwrite in editorMode
            lists: null, // NEW: map of value lists that can be referenced on the options
            idLink: true, // NEW: flag to skip adding _cl_options when false (default always linking)
            themes: [], // NEW: global themes with styles & custom css/js
        };

        this.config = config;

        // Merge options into defaults
        this.settings = Util.extend(defaults, options || {});

        // NEW: Util.extend does not keep original reference - so we reset it here (lazy config load can dynamically extend lists on global config)
        this.settings.lists = options.lists;

        // option visible states {name: true/false} ( mainly used to sync in editor )
        this.visible = {};
        this.visibleEditor = {}; // visible overwrite in editorMode

        /**
         * get shopify store ref to watch for variant changes
         * @type {ShopifyStore}
         */
        this.shopifyStore = ShopifyStore.getInstance();

        /**
         * get shopify cart ref to add price options product
         * @type {ShopifyCart}
         */
        this.shopifyCart = ShopifyCart.getInstance();

        // create new conditions engine - passed to product preview
        this.conditionsEngine = new ConditionsEngine({} , {
            product: this.settings.product,
            options: this.config.options
        });

        // upload for preview export uploads
        this._upload = Upload.getInstance({ api: this.settings.api, queryParams: this.settings.queryParams, errorPrefix: this.settings.errorPrefix + 'upload.' });

        // hacky: map settings to controller (if other controllers need access to settings we should find a better global abstraction)
        LibraryController.settings.api = this.settings.api;
        LibraryController.settings.assets = this.settings.assets;
        LibraryController.settings.queryParams = this.settings.queryParams;
        
        // Initialize AI controller settings
        AiController.settings.api = this.settings.api;
        AiController.settings.assets = this.settings.assets;
        AiController.settings.queryParams = this.settings.queryParams;

        // NEW: saved configurations core (the saved-configs option is only its UI - a configuration link works without the option)
        this._configs = new SavedConfigs(this);

        // global price options
        this.price = {
            options: {}, // key option.name
            total: 0,
            totalBase: 0, // NEW: total in base currency
            variants: [], // NEW: add product variants
            variantTotal: 0, // is subtracted from total (already added via the actual priceVariant product),
            weight: 0, // NEW: total weight of all selected options
            sku: [], // NEW: sku of all selected options,
            bundle: [], // NEW: variants to bundle
            bundleMain: 0, // NEW: main bundle variant price (discount on the main product)
        };

        // check if selector is already an element
        if(Util.isElement(this.settings.selector)){
            this.element = this.settings.selector;
        } else {
            this.element = document.querySelector(this.settings.selector);
        }

        if (this.element != null) {

            log.info('--> new product-options: ', this.element);

            // NEW: multi currency with price product add-on mode OR linked products on options - we have to fetch to get the pricing in the current currency
            if(ThemeCurrency.currencyConvert && ThemeCurrency.currencyFactor !== 1 ||
                // lookup stock to auto disable products that are out of stock
                (ThemeConfig.productLinkAvailable && !this.settings.editorMode) || // avoid in editor mode stock lookup UPDATE: now would be possible with new backend logic - but we keep it disabled to avoid unwanted side effects
                 // NEW: bundle products always requires that we fetch the accurate price AND editorMode needs to fetch the products as well to get variant info for price/img
                AppExtensions.BUNDLE
            ){
                // NEW: ready allows to wait for the init from outside (merge options)
                this.ready = this._variantsInCurrency().then(() => this._init());  // INFO: we only use promise if needed to keep old logic similar as before the change
            } else {
                this._init();
                this.ready = Promise.resolve(); // NEW: already initialized
            }
        } else {
            log.warn('no elements with selector found: ', this.settings.selector);
        }
    }

    destroy(){
        this.removeCss();

        // remove wrapper
        this.element.removeChild(this._wrapper);

        this._destroyPreview();

    }

    // backend editor
    updateVariantOptions(variantOptions, keepControllers = true){

        // NEW: controllers restore support (values are set by selected variant)
        let controllers;
        if(keepControllers){
            controllers = this._getControllers({}, this.config.variantOptions);
        }

        // overwrite directly (no reference should be passed anywhere)
        this.config.variantOptions = variantOptions;

        if(this._variantOptions){
            // update current variant (to select the right value)
            this._variantOptions.settings.variant = this._getVariantById(this._variantId);
            // build options again with the updated variantOptions
            this._variantOptions._init();
        }

        if(keepControllers){
            this._setControllers(controllers, this.config.variantOptions);
        }

        // no this.render()  - backend calls this before updateOptions
    }

    // mainly used in backend editor
    updateOptions(options, keepValues = true, keepControllers = true){

        let values, controllers;
        if(keepValues){
            values = this.getValues();
        }
        if(keepControllers){
            controllers = this._getControllers(); // NEW: controllers restore ( mainly to avoid upload field double init )
        }

        // update options in place to keep same reference
        this.config.options.length = 0;
        this.config.options.push(...options);

        // re-generate option ids
        this._generateOptionIds(this.config.options, 'option_' + this.settings.product.id + '_');

        // re-translate (keys are reset) IMPORTANT: call before _setControllers (to avoid circular .options dependency)
        this._translate.translate();

        if(keepValues){

            // NEW: clear empty values (support backend default update - only if not yet selected)
            for (const name in values) {
                if(!values[name]){
                    delete values[name];
                }
            }

           this.setValues(values);
        }
        if(keepControllers){
            this._setControllers(controllers);
        }

        // destroy tooltips to update with new content (only initialized once)
        this._destroyTooltips();

        // HACKY: bundle extension - fetch updated products before rendering
        if(AppExtensions.BUNDLE){

            return this._variantsInCurrency().then(() => {
                this.render();
            });
        }

        this.render();
    }

    updatePreview(previewConfig){
        this.config.preview = true; // implicit enabled

        // re-translate the view only
        this._translate._translatePreview(previewConfig);

        // always update reference so options (e.g. group-editor) can see new/updated layers
        this.config.previewConfig = previewConfig;

        // first time no preview
        if(!this._preview){
            this._initPreview();
        } else {
            // update the active preview (support for multi view)
            this._preview.updateConfig(previewConfig)
                // NEW: render after preview update (editorMode toggle crop/textEditor components that are shown in the options)
                .then(() => this.render());
        }
    }

    updateCss(css){
        this.removeCss();

        // for web component style can not be added in head ( styleSelector currently not used )
        this._style = Util.addStyleElement(css, this.settings.styleSelector);
    }

    removeCss(){
        if(this._style){
            // remove style from page
            this._style.parentNode.removeChild(this._style);
        }
    }

    updateJs(js){
        this.removeJs();

        // custom js execution only after options are rendered
        js = `window.addEventListener('options:rendered',function(e){ ${js} },{once:true});`

        this._script = Util.addScriptElement(js);
    }

    removeJs(){
        if(this._script){
            // remove script from page
            this._script.parentNode.removeChild(this._script);
        }
    }

    _isThemeMode() {
        return this.config.themeId && this.config.themeId !== 'local';
    }

    /**
     * Apply specific theme
     * @param theme {{ styles: object, css: string, js: string }} theme
     */
    applyTheme(theme) {

        let css = '';

        // generate css variables from styles
        if (Object.keys(theme.styles).length) {
            css = '#' + this.config.uuid + ' {\n';
            for (const [key, value] of Object.entries(theme.styles)) {
                if (key !== 'css' && value) {  // Skip 'css' key, it's custom CSS
                    css += '  --cl-po-' + key + ': ' + value + ';\n';
                }
            }
            css += '}\n';
        }

        if (theme.css) {
            css += '\n' + theme.css;
        }

        if (css) {
            this.updateCss(css);
        }
        if (theme.js) {
            this.updateJs(theme.js);
        }
    }

    validate() {

        let valid = this._validateOptions(this.config.options);

        log.debug('validated product-options: ',  this.config, valid);

        // NEW: event - currently only used by "tabs" option
        // INFO: event is sent before render to allow error customizations before render
        this._triggerEvent('options:validated', {valid: valid});

        // NEW: experimental to exclude _cl_options when no options are selected
        // INFO: backend order processing already skips processing of orders that have no option values - so this is only UX related to avoid showing the ID on the order
        if(ThemeConfig.productOptionsEmptyId){
            // link if any option value is set
            this.settings.idLink = this._hasOptionsValues();
        }

        // render errors
        this.render();

        return valid;
    }

    render(){

        let options = [];

        // NEW: try-catch around render
        try {
            // always evaluate calculated value (before conditions)
            this._evaluateOptionValue(this.config.options);

            // always evaluate conditions
            this._evaluateOptionConditions(this.config.options);

            // always evaluate options price
            this._evaluateOptionPrice(this.config.options);

            // custom id option used in editorMode
            if (this._variantIdOption) {
                options.push(this._variantIdOption);
            }
            // internal variant options rendering
            if (this._variantOptions && this.config.variantOptions.length) {
                options.push(...this.config.variantOptions);
            }

            const renderConfig = {
                uuid: this.config.uuid,
                editorMode: this.settings.editorMode,
                price: this.price,
                product: this.settings.product,
                variant: this.conditionsEngine.data.variant, // NEW: used on bundle extension - conditions engine already has the variant evaluated
                productForm: this._productForm,
                productFormLink: this._productFormLink,
                wrapper: this._wrapper, // NEW: wrapper reference to allow controllers find their own option element (product form element can not contain the options - formLink only)
                assets: this.settings.assets, // assets used for lazy loading
                idLink: this.settings.idLink, // skip adding _cl_options when false
                previewConfig: this.config.preview ? this.config.previewConfig : undefined, // NEW: reference for GroupEditor dynamic preview modifications
                configs: this._configs, // NEW: saved configurations core for the saved-configs option
                _priceFunctionUtil: this._priceFunctionUtil,
            };

            options.push(...this.config.options);

            // NEW: pre render event (b2b app uses to patch prices with discounts) 'options:render' already used to trigger new render
            this._triggerEvent('options:pre_render', this);

            // efficient render of options into wrapper - can be called at any time - only updates changes
            render(productOptionsTemplate(options, renderConfig, this._optionValueChanged.bind(this), this._clearOptionError.bind(this), this.validate.bind(this), this._getOption.bind(this)), this._wrapper);

            // event ( used on server to sync visible states )
            this._triggerEvent('options:rendered', this);

            // we have to check if there are new tooltips on every render
            this._initTooltips();

        } catch(error){
            log.error('failed to render product options', options, error);
        }
    }

    // default params to support nested
    getValues(values = {}, options = this.config.options){

        for (let i = 0; i < options.length; i++) {
            const option = options[i];

            // nested
            if(option.options && option.options.length){
                // we pass values reference
                this.getValues(values, option.options);
            } else {
                values[option.name] = option.value;
            }
        }

        return values;
    }

    // useful to reload state from external
    setValues(values, options = this.config.options){

        // update option values for
        for (let i = 0; i < options.length; i++) {
            const option = options[i];

            // nested (exclude group-editor which handles its own restore via _restoreValues)
            if(option.options && option.options.length && option.type !== 'group-editor'){
                // we pass values reference
                this.setValues(values, option.options);
            } else if(values.hasOwnProperty(option.name)) {
                option.value = values[option.name];
            } else if(option.type === 'upload-lift'){
               // special multi select case, e.g "upload_1" "upload_2" => combine into "upload" = "<>,<>"
                // TODO existing bug ( separate PR ): indexOf matches any key containing the name ( e.g "upload_size", "_upload_id" ) - use numbered()
                let urls = [];
                for (const property in values) {
                    if(values.hasOwnProperty(property) && property.indexOf(option.name + '_') !== -1){
                        urls.push(values[property]);
                    }
                }
                if(urls.length){
                    option.value = urls.join(',');
                }
            } else if(option.type === 'upload'){
                // same for the plain upload field, e.g "photo_1" "photo_2" => "photo" = "<>,<>" ( the controller rebuilds one input per file )
                let urls = [];
                for (const property in values) {
                    if(values.hasOwnProperty(property) && numbered(property, option.name) && values[property]){
                        urls.push(values[property]);
                    }
                }
                if(urls.length){
                    option.value = urls.join(',');
                }
            } else if(option.type === 'group-editor'){
                // Group-editor stores instance values (e.g., upload_1, upload_2)
                // Store the full values object on the option for later restore
                option._restoreValues = values;
            }

            // NEW: library external image id restore support
            if(option.type === 'library' && values.hasOwnProperty('_' + option.name + '_id')){
                option._imageId = values['_' + option.name + '_id'];
            }

            // NEW: text editor restore support
            const editorProperty = '_' + option.name + '_editor';
            if(values.hasOwnProperty(editorProperty)){
                const editorPatch = parseTextEditorProperty(values[editorProperty]);
                if(Object.keys(editorPatch).length){
                    option._restoreEditor = Util.extend(option._restoreEditor || {}, editorPatch);
                } else {
                    delete option._restoreEditor;
                }
            } else {
                delete option._restoreEditor;
            }
        }
    }

    // public: setValues + render (conditions/prices/DOM) + preview refresh > resolves after the previews are rendered (no option:changed events)
    // IMPORTANT: same values render again (emits options:rendered) > guard calls inside recurring options:rendered handlers
    updateValues(values){
        this.setValues(values);
        this.render();

        // deferred: the first options:rendered fires before _initPreview & this.ready exist (config js)
        return Promise.resolve().then(() => this.ready).then(() => this._renderPreview());
    }

    // public: current Shopify variant or null when unresolved - live object, read-only for integrations
    getSelectedVariant(){
        return this._variantId == null ? null : this._getVariantById(this._variantId);
    }

    // public: translation for key in the current language, {var} placeholders replaced from vars > fallback (or key) when missing
    translate(key, vars, fallback){
        return Translate.get(key, vars, fallback);
    }

    _init() {

        // create wrapper to scope by uuid
        this._wrapper = document.createElement('div');
        this._wrapper.className = this.settings.classNamePrefix + '--wrapper';
        this._wrapper.setAttribute('id', this.config.uuid); // INFO: id is not unique when multiple products are on a single page .. seems to work anyway ( used for CSS scoping )

        // Apply styling based on mode
        if (this._isThemeMode()) {

            // apply selected theme
            let theme = this.settings.themes?.find(t => t.id === this.config.themeId);
            if(theme){
                this.applyTheme(theme);
            }

        } else {
            // Local mode: use pre-compiled CSS from backend
            if (this.config.css) {
                this.updateCss(this.config.css);
            }
            // NEW: add js
            if (this.config.js) {
                this.updateJs(this.config.js);
            }
        }

        // add wrapper to element with position support
        //https://developer.mozilla.org/en-US/docs/Web/API/Element/insertAdjacentElement
        if(this.settings.position){
            this.element.insertAdjacentElement(this.settings.position, this._wrapper);
            // case before/after -> update parent element (required for events & all this.element.querySelector methods)
            if(this.element !== this._wrapper.parentNode){
                this.element = this._wrapper.parentNode;
            }
        } else {
            // default child
            this.element.appendChild(this._wrapper);
        }

        // generate unique ids for each option ( used for validation and references for the labels )
        this._generateOptionIds(this.config.options, 'option_' + this.settings.product.id + '_'); // prefix with product id to be unique if multiple products with the same options are on a single page

        // clear options after added to cart
        if(ThemeConfig.productOptionsClear){
            // get initial values to re-set to the initial state (contains default values)
            this._optionValuesClear = this.getValues();
        }

        if(this._findProductForm()){

            // INFO: currently options are translated once to simplify logic
            this._translate = new ConfigTranslate(this.config);
            this._translate.translate();

            // disable html form validation (we manually handle it) (most themes have this already disabled)
            this._productForm.noValidate = true;

            // render listener to trigger render from option controllers
            this._productForm.addEventListener('options:render', (e) => this.render());
            // validate listener to trigger validate from option controllers
            this._productForm.addEventListener('options:validate', (e) => this.validate());

            this._watchFormSubmit();

            // triggers variant change > render
            this._watchVariantChanges();

            if(this.config.preview) {
                this._initPreview();
            }

            if(Util.hasQueryParam(window.location.href, 'item')){
                // NEW: work stashed before the login comes back on top of the cart item
                this._restoreCartItem().then(() => this._configs.resume());
            } else if(this._configs.resume()){
                // work stashed before the login / reload wins over the links
                log.debug('restored values stashed before the login');
            } else if(SavedConfigs.recoverParam()){
                // NEW: recovery link - the newest saved configuration of this product, before the configuration link
                this._configs.recoverFromUrl();
            } else if(SavedConfigs.urlParam(Order.RESTORE_PARAM) && SavedConfigs.urlParam(Order.LINE_PARAM)){
                // NEW: buy again per line on the product page - the ordered line opens on the instance of its configuration
                this._openOrderLineFromUrl();
            } else if(SavedConfigs.urlParam()){
                // NEW: saved configuration link - after the cart item, before the prefill
                this._configs.openFromUrl();
            } else if(Util.hasQueryParam(window.location.href, '_f')){
                // only prefill from URL if not restoring a cart item (cart item takes priority)
                this._prefillFromUrl();
            }

            // NEW: share link generation
            window.addEventListener('options:share', (e) => {
                // generate share link popup
                let link = this._generatePermalink();
                if(e.detail?.url !== false){ // default to update address bar
                    // update URL in address bar without reloading
                    window.history.replaceState({}, '', link);
                }
                this._triggerEvent('options:shared', { link: link });
            });

        } else {
            log.warn('product-options no product form found for element: ', this.element,  this.config);
        }

    }

    _variantsInCurrency(options = this.config.options, lists = this.settings.lists){

        let productPromises = [];

        // NEW: cache if same products are referenced multiple times
        let promiseCache = {};

        // INFO: add-on product used for option prices can have different price based on the current currency > we have to fetch it in the current currency to calculate the right add-on prices
        if(ThemeCurrency.priceVariant) {
            productPromises.push(this.shopifyStore.getProduct(ThemeCurrency.priceProductHandle || 'product-options').then((product) => {
                ThemeCurrency.priceVariantValue = product.price / 100;
            }).catch(() => true));
        }

        // INFO: we have to get the converted price for linked optionValue products - used to show the right price on UI & calculate this.price.variantTotal in current currency
        const fetchVariantCurrencyPrice = (optionValue) => {
            let priceVariant = optionValue.priceVariant;
            let handle = priceVariant.handle;
            if(!handle && priceVariant.title){ // previous version did not save the variant
                handle = priceVariant.title.substring(0, priceVariant.title.indexOf('-')).trim().replaceAll(' ', '-').toLowerCase();
            }
            if(handle){

                // fetch product only once per handle
                promiseCache[handle] = promiseCache[handle] || this.shopifyStore.getProduct(handle);

                productPromises.push(promiseCache[handle].then((product) => {
                    // find the exact variant
                    if(product.variants){
                        for (let i = 0; i < product.variants.length; i++) {
                            const variant = product.variants[i];
                            if(variant.id.toString() === priceVariant.id.toString()){

                                // we update the optionValue.price to be in the current currency
                                // NEW: priceVariantBundlePrice keeps the merchant defined price (lookup only used for stock check)
                                if(!optionValue.priceVariantBundlePrice){
                                    optionValue.price = variant.price / 100;
                                }

                                // NEW: map variant available as disabled
                                if(ThemeConfig.productLinkAvailable){

                                    // NEW: update price * quantity to show on UI (backend does the same - without stock update)
                                    if(optionValue.priceVariant?.quantity > 1){
                                        // store single product price value (required to re-calculate when quantity updates)
                                        if(!optionValue.hasOwnProperty('_price')){ // only init once
                                            optionValue._price = optionValue.price;
                                        }
                                        optionValue.price = optionValue._price * optionValue.priceVariant.quantity;
                                    }

                                    optionValue.disabled = !variant.available;
                                }
                                break;
                            }
                        }
                    }

                }).catch((error) => {
                    log.warn('missing linked product for optionValue: ', optionValue, error);
                    // product not found
                    if(ThemeConfig.productLinkAvailable){
                        optionValue.disabled = true;
                    }
                }));
            }
        }

        const checkOptionValues = (option) => {
            let optionValues = option.values;

            // IMPORTANT: we check list and modify the global list before generateOptionIds (original list resolve location with deepCopy)
            if(option.list && lists){ // NEW: allow outside overwrite (exclude) of lists (merge calls generateOptionIds before this)
                // if found > use values defined in list
                if(lists.hasOwnProperty(option.list)){
                    optionValues = lists[option.list];
                }
            }

            if(optionValues){
                for (let i = 0; i < optionValues.length; i++) {
                    const optionValue = optionValues[i];
                    if(optionValue.priceVariant){
                        // NEW: priceVariantBundlePrice - keep merchant defined price in base currency (charged via bundle cart transform)
                        if(optionValue.priceVariantBundlePrice && AppExtensions.BUNDLE){

                            // store original base price once (re-runs convert again from base)
                            if(!optionValue.hasOwnProperty('_priceBase')){
                                optionValue._priceBase = optionValue.price;
                            }

                            // single unit price in current currency (bundle price + quantityInput calculate with _price)
                            optionValue._price = ThemeCurrency.currencyConvert ? currencyConvert(optionValue._priceBase) : optionValue._priceBase;
                            optionValue.price = optionValue._price * (optionValue.priceVariant.quantity || 1);

                            // stock check still requires the product lookup (price update skipped via priceVariantBundlePrice)
                            if(ThemeConfig.productLinkAvailable){
                                fetchVariantCurrencyPrice(optionValue);
                            }
                        } else {
                            // priceVariantBundlePrice is only charged by the bundle cart transform - separate add to cart always charges the product price
                            if(optionValue.priceVariantBundlePrice){
                                optionValue.priceVariantBundlePrice = false;
                                log.warn('[live-options] priceVariantBundlePrice requires the bundle extension - fallback to product price', optionValue);
                            }
                            fetchVariantCurrencyPrice(optionValue);
                        }
                    }
                }
            }
        }

        // nested (group > group-editor > option)
        const checkOptionsNested = (optionsNested) => {
            for (let i = 0; i < optionsNested.length; i++) {
                const option = optionsNested[i];
                checkOptionValues(option);
                if(option.options && option.options.length){
                    checkOptionsNested(option.options);
                }
            }
        }

        checkOptionsNested(options);

        return Promise.all(productPromises);
    }

    _validateOptions(options, scrolled = false){  // scrolled flag to only scroll to the first element that is invalid in the case its not visible

        let valid = true;

        for (let i = 0; i < options.length; i++) {
            const option = options[i];

            // only validate visible options
            if(!option.visible){
                continue;
            }

            // nested
            if(option.options && option.options.length){
                let result = this._validateOptions(option.options, scrolled);
                // important only set invalid - valid would reset previous invalid state
                if(!result){
                    valid = false;
                }

                // NEW: parent-level controller validation (e.g. group-editor min)
                if(option._controller && typeof option._controller.onValidate === 'function'){
                    option.errors = [];
                    let result = option._controller.onValidate(option);
                    if(!result){ valid = false; }
                }
            } else {

                let element = this.element;

                // NEW: popup personalize view different parent element to find options
                if(this._popup && this._popup.isOn() && this._popupEl){
                    element = this._popupEl;
                }

                // NEW: errors from price formula (always show once and set invalid)
                // INFO: currently errors are not correctly reset if another option error is set and the option is not changed
                if(option.errors?.length && option._errorFormula){
                    // only show once (on render evaluated again)
                    option.errors = [];
                    delete option._errorFormula;
                    valid = false;
                    continue;
                }

                // lookup via id selector
                let optionEl = element.querySelector(`#${option.id}`);  // lookup via id selector

                // custom option controller validate function
                if(option._controller && typeof option._controller.onValidate === 'function'){
                    // reset errors
                    option.errors = [];

                    let result = option._controller.onValidate(option);
                    // important only set invalid - valid would reset previous invalid state
                    if(!result){
                        valid = false;

                        // fallback to lookup option wrapper by name (only used to scroll into view below)
                        if(optionEl == null){
                            optionEl = element.querySelector(`[data-option="${option.name}"]`);
                        }
                    }
                }
                // form based validation
                else {

                    // INFO: radio validation has multiple items ( we can validate the first item which holds the validity of all radios in a group )
                    if(optionEl == null){                   // NEW: lookup via ID prefix AND exclude disabled (required validation is ignored on disabled elements)
                        optionEl = element.querySelector(`input[id^=${option.id}-]:not([disabled])`);
                    }

                    if(optionEl != null){
                        // validate via the browser api
                        if(!optionEl.checkValidity()) {

                            valid = false;

                            // reset errors
                            option.errors = [];

                            // custom error message > show only this message
                            if(option.params.validationMsg){
                                option.errors.push(option.params.validationMsg);
                                continue;
                            }

                            for(const validity in optionEl.validity){

                                // only if the flag is true
                                if(optionEl.validity[validity]){ // IMPORTANT: no hasOwnProperty ..
                                    let errorMsg = '';
                                    // add error translation - support for custom parameters
                                    if(validity === 'tooLong'){
                                        errorMsg = Translate.get(this.settings.errorPrefix + validity, { max: optionEl.maxLength });
                                    } else if(validity === 'tooShort'){
                                        errorMsg = Translate.get(this.settings.errorPrefix + validity, { min: optionEl.minLength });
                                    } else if(validity === 'rangeOverflow'){
                                        errorMsg = Translate.get(this.settings.errorPrefix + validity, { max: optionEl.max });
                                    } else if(validity === 'rangeUnderflow'){
                                        errorMsg = Translate.get(this.settings.errorPrefix + validity, { min: optionEl.min });
                                    } else if(validity === 'patternMismatch'){
                                        errorMsg = Translate.get(this.settings.errorPrefix + validity, { pattern: optionEl.pattern });
                                    } else {
                                        errorMsg = Translate.get(this.settings.errorPrefix + validity);
                                    }

                                    // only add if translation found
                                    if(errorMsg){
                                        if(errorMsg.indexOf(this.settings.errorPrefix) === -1){
                                            option.errors.push(errorMsg);
                                        } else {
                                            log.warn('missing translation for ' + errorMsg);
                                        }
                                    }
                                }

                            }

                        }
                    }
                }

                if(!valid && optionEl){
                    // first invalid element -> scroll into view if not visible
                    if(!scrolled && optionEl.scrollIntoView){

                        // NEW: hacky way of auto opening collapsed group if not open
                        let panelEl = optionEl.closest('.cl-po--panel');
                        if(panelEl && !panelEl.previousElementSibling.classList.contains('cl-po--open')){
                            panelEl.previousElementSibling.classList.add('cl-po--open');
                        }

                        // NEW: scroll to the option wrapper (not the input element)
                        let optionWrapperEl = optionEl.closest('[data-option]');

                        // check if visible (after de-collapse)
                        if(!Util.isScrolledIntoView(optionWrapperEl)){
                            // INFO: sticky preview can cause overlay, no simply fix here so we just allow scroll offset
                            optionWrapperEl.style.scrollMargin = window.clOptionScrollOffset || '100px'; // this makes the scrollIntoView a bit above the element
                            optionWrapperEl.scrollIntoView({behavior: "smooth", block: "start", inline: "nearest"});
                            optionWrapperEl.style.scrollMargin = '';
                            scrolled = true;
                        }
                    }
                }

            }

        }

        return valid;
    }

    _findProductForm(){
        // get product form by parent
        // INFO: element always has to be a child of a product form for the properties to be saved
        this._productForm = Util.findParent(this.element, this.settings.selectorForm);

        // fallback ( NEW: online store 2.0 app blocks can be outside form )
        if(!this._productForm){
            let searchOptions = { srcEl: this.element };
            this._productForm = this.shopifyStore.findProductFormBestMatch(this.settings.product.id, searchOptions);
            // parent element shared with this element and product form
            if(searchOptions.parentEl){
                // store to pass to the ProductVariantOptions to find the themes variant options that are outside of the productForm
                this._productFormParentEl = searchOptions.parentEl;
            }
        }

        // NEW: flag if productForm is a parent
        this._productFormParent = this._productForm.contains(this._wrapper);

        // NEW: from link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-form
        // only link if form is not parent of this element
        if(!this._productFormParent || this._productFormLinkForce){
            let formId = this._productForm.getAttribute('id');
            // IMPORTANT: only add if defined - empty or wrong reference will break from properties being added to the cart
            if(formId){
                this._productFormLink = formId;
            }

            // NEW: paypal express does not save the properties when the inputs are outside the form > we have to hide the express buttons
            let expressPaymentBtns = this.shopifyStore.findExpressPaymentButtons();

            if(expressPaymentBtns.length){
                // NEW: we keep buy now buttons - but capture all clicks and handle the checkout ourself even if there are not price add-ons (as properties are not included - this can be removed as soon as the buy now button supports blocks)
                this._buyNowCapture = true;

                /* DEPRECATED for capture
                log.debug('hiding expressPaymentButtons - NOT supported with app blocks', expressPaymentBtns)
                for (let i = 0; i < expressPaymentBtns.length; i++) {
                    const expressBtn = expressPaymentBtns[i];
                    expressBtn.style.display = 'none';
                } */
            }
        }

        // NEW: paypal new smart button uses an iframe that makes it impossible to handle the click event - for now only solution is to hide it
        let paypalBuyNow = document.querySelector('shopify-paypal-button');
        if(paypalBuyNow){
            // hide paypal buy now button (same as above)
            paypalBuyNow.style.display = 'none';
            log.warn('[live-options] hiding paypal buy now');
        }

        return this._productForm !== null;
    }

    _initVariantOptions(){

        let variantId = this.shopifyStore.findProductFormVariantId(this._productForm, this._productFormParentEl);

        // backend setup no product selected
        if(this.settings.product && this.settings.product.variants){

            // if not found in theme - select first variant
            if(!variantId){
                variantId = this.settings.product.variants[0].id.toString();
            }

            // custom variant options flag & more than 1 variant
            if(this.config.variantOption && this.settings.product.variants.length > 1){

                // create variant options (updates theme & builds this.config.variantOptions) // NEW: productForm parent wrapper online store 2.0 support
                this._variantOptions = new ProductVariantOptions(this.config, this._productFormParentEl || this._productForm,{
                    product: this.settings.product,
                    variant: this._getVariantById(variantId), // pass current variant to pre select the right option.value
                    editorMode: this.settings.editorMode
                });

                if(ThemeConfig.productVariantFilter){
                    this._variantOptions.filterVariantValues();
                }
            }

            // id option - only add in editorMode (to support variant changes/conditions)
            if(this.settings.editorMode){

                this._variantIdOption = {
                    name: 'id',
                    id: 'option_' + this.settings.product.id + '_id',
                    value: variantId, // pre select first variant
                    params: {
                        name: 'id', // explicit name="id"  (same as in the shopify themes - will be used to add the selected variant to the cart)
                        info: '<b>Only shown in the App to test conditional options based on the selected variant.</b>'
                    },
                    visible: true
                }

                // single variant OR variant options are added via app
                if(this.settings.product.variants.length === 1 || this.config.variantOption){
                    this._variantIdOption.type = 'hidden';
                } else {
                    this._variantIdOption.label = 'Variant'; // only show label if select
                    this._variantIdOption.type = 'select';
                    this._variantIdOption.values = [];
                    for (let i = 0; i < this.settings.product.variants.length; i++) {
                        const variant = this.settings.product.variants[i];
                        this._variantIdOption.values.push({
                            text: variant.title,
                            value: variant.id.toString()
                        });
                    }
                }
            }

        }

        return variantId;
    }

    _watchVariantChanges(){

        // initialize options for variants
        let variantId = this._initVariantOptions();

        // NEW: theme specific variant change listener to get access to 2048 variants
        if (this.settings.product.variants?.length === 250) { // 250 is max variants returned by AJAX - we assume there are more variants
            // IMPORTANT: call before _onVariantChange to have the variants available
            this._extendVariants(variantId);
        }

        // apply initial selected variant
        this._onVariantChange(null, variantId);

        // watch for all form change events ( instead of watching the id itself which would be more efficient but less robust )
        if(ThemeConfig.productVariantChange){ // NEW: flag - some edge cases (PageFly) trigger change events before the "input" event is able to store the option.value
            this._productForm.addEventListener('change', (e) => this._onVariantChange(e));
        }

        // NEW: some themes have the variant ID element outside the product form in that case we watch directly on the variant ID element for changes
        // INFO: the above listener could also be changed to (this._productFormParentEl || this._productForm) - but this would cause all change events from the custom options to trigger a variant change
        if(this._productFormParentEl){ // only required if a custom form parent (app blocks) are used
            this.shopifyStore.onVariantIdChange(this._productFormParentEl, (e, variantId) => this._onVariantChange(e, variantId));
        }

        // NEW: if theme does not trigger variant changes - watch browser history for changes
        if(ThemeConfig.productVariantHistory){
            // depending on the theme pushState or replaceState is called: https://developer.mozilla.org/en-US/docs/Web/API/History#methods

            // DEFAULT: watch both history methods
            if(ThemeConfig.productVariantHistoryPush){
                this._watchVariantHistory('pushState');
            }
            if(ThemeConfig.productVariantHistoryReplace){
                this._watchVariantHistory('replaceState');
            }
        }

        // return true if variant found
        return this._variantId !== null;
    }

    _watchVariantHistory(patchMethod){
        // check if patch method is available
        if(typeof window.history[patchMethod] === 'function'){

            const original = window.history[patchMethod]; // reference to the unpatched method
            const form = this._productForm; // local variable to refer to this context

            // wrap function call
            window.history[patchMethod] = function(state) {
                setTimeout(function(){

                    // manually trigger change > triggers app
                    form.dispatchEvent(new Event('change'));

                }, 300);  // timeout to wait for variant id update

                // call original
                return original.apply(window.history, arguments);
            };
        }
    }

    _onVariantChange(event, variantId) {
        // default resolve via form
        if(!variantId){
            variantId = this.shopifyStore.findProductFormVariantId(this._productForm, this._productFormParentEl);
        }
        // check if the variant id changed
        if (variantId !== this._variantId) {

            // can be null if not found
            this._variantId = variantId;

            // clear old variant
            delete this.conditionsEngine.data.variant;

            if(this._variantId){

                // update variant in conditions data
                let variant = this._getVariantById(this._variantId);
                if(variant){
                    this.conditionsEngine.data.variant = variant;
                }

                // clear visible overwrite ( to show based on conditions )
                this._clearVisibleEditor();

                // trigger changed event to watch from outside
                this._triggerEvent('option:variant_changed', { variantId: this._variantId });
            }

            this.render();
        }
    }

    _extendVariants(variantId){
        const extendVariant = (e, variant) => {
            if (variant?.hasOwnProperty('id')) {
                // check if variant is already in the list
                let existingVariant = this._getVariantById(variant.id);
                if (!existingVariant) {
                    // add to the list of variants
                    this.settings.product.variants.push(variant);
                    // trigger change again to make sure variant conditions are working
                    this._onVariantChange(e, variant.id);
                }
            }
        };
        const extendVariants = (e) => {
            if (e.detail) {
                let variant = null;
                // Dawn
                if (e.detail.variant?.hasOwnProperty('id')) {
                    variant = e.detail.variant;
                }
                // Impact: direct variant in detail
                else if (e.detail.hasOwnProperty('id')) {
                    variant = e.detail;
                }
                extendVariant(e, variant);
            }
        };
        //                              Dawn theme       Impact
        const themeEvents = ['variant-change', 'variant:change'];
        themeEvents.forEach(eventName => {
            window.addEventListener(eventName, extendVariants);
        });
        // Standard storefront product selection + Horizon 1.x-3.x legacy support
        // (keep the existing global event scope)
        this.shopifyStore.onProductVariantUpdate(window, (e, update) => extendVariant(e, update.variant));

        // check already selected variant
        if (variantId) {
            let selectedVariant = this._getVariantById(variantId);
            if (!selectedVariant) {
                // find variant in theme (if available)                                  Impact, Dawn, Horizon (not specific: https://github.com/Shopify/horizon/blob/main/snippets/variant-main-picker.liquid#L183)
                let variantJson = document.querySelectorAll('script[data-variant], script[data-selected-variant], script[type="application/json"]');
                if (variantJson.length) {
                    for (let i = 0; i < variantJson.length; i++) {
                        const variantEl = variantJson[i];
                        try {
                            let variantData = JSON.parse(variantEl.textContent);
                            if (variantData && variantData.id?.toString() === variantId?.toString()) {
                                this.settings.product.variants.push(variantData);
                                break;
                            }
                        } catch (error) {
                            // not json
                        }
                    }
                }
            }
        }
    }

    _getVariantById(variantId){
        // update variant in conditions data
        if(this.settings.product.variants && this.settings.product.variants.length > 0){
            for (let k = 0; k < this.settings.product.variants.length; k++) {
                const variant = this.settings.product.variants[k];
                // compare with current variantId
                if(variant.id.toString() === variantId.toString()){
                    return variant;
                }
            }
        }
        return null;
    }

    _triggerEvent(name, data){
        // custom event allow cancel via e.preventDefault & bubble to trigger up the DOM tree
        const event = new CustomEvent(name, { detail: data, cancelable: true, bubbles: true });
        // we currently use the root element to bubble up to the productForm
        this.element.dispatchEvent(event);
        // NEW: productForm not in parent > trigger event separately on productForm
        if(!this._productFormParent){
            this._productForm.dispatchEvent(event);
        }
        return event;
    }

    _optionValueChanged(option){

        // check if a variant option was changed
        if(option._variantOption && this._variantOptions){
            this._variantOptionValueChanged(option);
        }

        // clear visible overwrite ( to show based on conditions )
        this._clearVisibleEditor();

        // we clear all the errors on update ( alternative would be to re-run validations of this option - but overkill )  NEW: reset before option:changed event
        if(option.errors && option.errors.length){
            // NEW: only reset if there is no new error ( required to show real time validations after value change - not only after add to cart validate)
            if(!option._error){
                option.errors.length = 0;
            } else {
                // reset error flag
                delete option._error;
            }
        }

        // trigger changed event to watch from outside ( support for outside value changes )
        this._triggerEvent('option:changed', option);

        this.render();
    }

    _variantOptionValueChanged(option){
        let productVariant = this._variantOptions.findSelectedVariant();

        // current selection has no matching variant
        if(!productVariant){
            this._variantOptions.resetVariant(option);
            this._variantOptions.syncAllVariantOptions();  // required to update to the new variant - should trigger onVariantChange
        }

        // re-filter variants only if more than one level of variants (multiple combinations)
        if(ThemeConfig.productVariantFilter && this.config.variantOptions.length > 1){
            this._variantOptions.filterVariantValues();
        }

        // sync with theme variants (required to update price and update media gallery)
        this._variantOptions.syncVariantChanges(option);

        // manual variant id update
        if(this._variantIdOption){
            if(productVariant){
                this._variantIdOption.value = productVariant.id.toString();
                this._onVariantChange(null, this._variantIdOption.value); // update directly
            }
        }
    }

    _clearVisibleEditor(){
        // reset the overwritten visible states to use the evaluated states from the conditions
        if(this.settings.editorMode){
            this.visibleEditor = {};
        }
    }

    _clearOptionError(e, option, error){
        // find error and splice
        const index = option.errors.indexOf(error);
        if (index > -1) {
            option.errors.splice(index, 1);
        }

        // removes the error from the dom
        this.render();
    }

    _generateOptionIds(options, prefix, initPersonalize = true, startIndex = 0) {

        // Pre-process special wrapper options in priority order
        if(initPersonalize) {
            // 1. First check for personalize option (highest priority)
            let personalizeOption = options.find(o => o.type === 'personalize');
            if(personalizeOption) {
                let optionsPersonalize = this._initPersonalize(personalizeOption);
                // return and build ids on personalize options (keep initPersonalize=true to allow tabs to process)
                return this._generateOptionIds(optionsPersonalize, prefix, true, startIndex);
            }

            // 2. Then check for tabs option (only runs if no personalize found)
            let tabsOption = options.find(o => o.type === 'tabs');
            if(tabsOption) {
                let optionsTabs = this._initTabs(tabsOption);
                // return and build ids on tabs options
                return this._generateOptionIds(optionsTabs, prefix, true, startIndex);
            }
        }

        for (let i = 0; i < options.length; i++) {

            const option = options[i];

            /* DEPRECATED: option id based on name

            // clean name to only contain allowed id chars - NEW allow numbers
            // OLD: let name = option.name.replace(/^[^a-zA-Z0-9]+|[^\w]+/g, "");
            // NEW: remove all non-allowed characters (including non-breaking space)
            let name = option.name.replace(/^[^a-zA-Z]+|[^a-zA-Z0-9_\u00A0-\uFFFF-]/g, "");

            // NEW: all special characters are replaced can result in empty name
            if(name === ''){
                name = String(i); // fallback to index
            }*/

            // NEW: always use index to NEVER have the same option ID
            let name = String(startIndex + i);

            // build id based on prefix + name
            option.id = prefix + name;

            // NEW: option global list reference
            if(option.list && this.settings.lists){
                // if found > use values defined in list
                if(this.settings.lists.hasOwnProperty(option.list)){
                    // deep copy to avoid and reference issues (list can be re-used across options)
                    option.values = JSON.parse(JSON.stringify(this.settings.lists[option.list]));
                }
            }

            // NEW: personalize option custom wrap of options
            if(option.type === 'personalize' && initPersonalize){
                let optionsPersonalize = this._initPersonalize(option);
                // return and build ids on personalize options
                return this._generateOptionIds(optionsPersonalize, prefix, false, startIndex + 1); // force not use of 0 to avoid id conflicts
            }

            // nested
            if(option.options && option.options.length){
                this._generateOptionIds(option.options, option.id + '_'); // NEW nested prefix with option id
            }

        }
    }

    _evaluateOptionConditions(options){

        for (let i = 0; i < options.length; i++) {

            const option = options[i];

            let visible = true;

            // check if option is conditional
            if(option.conditions && option.conditions.length){                                     // NEW: conditionMode config - default AND
                visible = this.conditionsEngine.evaluateConditions(option.conditions, option.conditionMode || ConditionsEngine.MODE.AND, 'option.name=' + option.name);
            }

            // editor can overwrite visible state
            if(this.settings.editorMode){
                if(this.visibleEditor.hasOwnProperty(option.name)){
                    visible = this.visibleEditor[option.name];
                }
            }

            // store visible state  IMPORTANT: after editorMode to sync back
            this.visible[option.name] = visible;

            // set visible flag for rendering in template
            option.visible = visible;

            // nested
            if(option.options && option.options.length){
                this._evaluateOptionConditions(option.options);
            }
        }
    }

    _evaluateOptionPrice(options){

        // clear all calculated price options
        this.price.options = {};
        this.price.total = 0;
        this.price.totalBase = 0;
        this.price.variants = [];
        this.price.variantTotal = 0;
        this.price.weight = 0;
        this.price.final = 0; // total + variantPrice
        this.price.sku = [];
        this.price.bundle = [];
        this.price.bundleMain = 0;

        // NEW: readonly price total object (referenced to store price on add to cart)
        // INFO: we created a new object instead of refactoring the existing price object to avoid breaking changes
        this._pr = this._pp({ total: 0, totalBase: 0, variantTotal: 0 });

        // NEW: price formula state - reset before every evaluation
        this._pf = { qty: undefined, usedQty: false, divideByQty: false };

        // calc total
        this._evaluateOptionPriceTotal(options);

        // convert the summed base once like the cart transform - per-option rounding does not add up (9.12 + 18.24 > 9.20 + 19)
        if(ThemeCurrency.currencyConvert && this._pr.totalBase){
            const totalConverted = currencyConvert(this._pr.totalBase);
            this._pr = this._pp({ ...this._pr, total: this._pr.total + totalConverted });
            this.price.total += totalConverted;
        }

        // BETA: subtract main variant price from total (edge case when options should fully control the price)
        if(ThemeConfig.productPriceDeductVP){

            // update the readonly price object
            this._pr = this._pp({ ...this._pr, total: this._dVP(this._pr.total, false), totalBase: this._dVP(this._pr.totalBase) });

            // UI uses the original price object
            this.price.total = this._pr.total;
            this.price.totalBase = this._pr.totalBase;
        }

        // update if total changed
        this._patchProductPriceTotal();
        this._patchProductWeightTotal();
        this._patchProductSku();
    }

    _patchProductPriceTotal(){

        // NEW: init with previous variant price from last update (edge case when variantId is null because out of stock)
        let variantPrice = this._variantPrice;

        // total price update requires selected variant price
        if(this._variantId) {

            // get variant to get the base price
            let variant = this._getVariantById(this._variantId);

            if (variant) {
                variantPrice = variant.price;
                // save the variant price - required to update the price if the variant is out so stock
                this._variantPrice = variantPrice;

                // NEW: compare at price support (resets to null if missing)
                this._variantCompareAtPrice = variant.compare_at_price;

                // HACKY: bundle main product price overwrite
                if(AppExtensions.BUNDLE && this.price.bundleMain !== 0){
                    variantPrice = this.price.bundleMain * 100;
                }
            }
        }

        // IMPORTANT: number check , variantPrice can be 0
        if(!isNaN(variantPrice)) {

            // NEW: we always calculate the final price even if this.price.total == 0 (no add-ons) REQUIRED for quantity-discount option to show discount based on current price
            // store final price (add-ons + variant)
            this.price.final = this.price.total + (variantPrice / 100);

            // only update price if options price is set OR if the price was updated previously  OR variant deduct always update (some merchants require options:price event)
            if(this.price.total !== 0 || (this._priceElList && this._priceElList.length) || ThemeConfig.productPriceDeductVP){

                // previous found element => check if still in page (some themes completely replace parts of the page)
                if(this._priceElList && this._priceElList.length){
                    for (let i = 0; i < this._priceElList.length; i++) {
                        if(!Util.isInPage(this._priceElList[i])){
                            delete this._priceElList; // delete to resolve again
                            break; // full list is deleted
                        }
                    }
                }

                if(!this._priceElList || this._priceElList.length === 0){
                    // specific selector with product id, // data-live-price="{{ product.id }}"
                    // NEW: ALL to support themes showing prices on multiple locations (e.g top and bottom on cart button)
                    this._priceElList = document.querySelectorAll('[data-live-price="'+ this.settings.product.id+'"]');

                    // not found => search based on price                         && avoid replacing wrong parts with price in editor backend
                    if(this._priceElList.length === 0 && ThemeConfig.productPrice && !this.settings.editorMode) {

                        // get price formatted in theme currency with amount only  (for better search success)
                        let priceMoney = formatMoney(variantPrice, this.shopifyStore.trimMoneyFormatAmount(ThemeCurrency.moneyFormat));

                        // search all elements that contain the variant price as its direct textContent and with max 50 characters before and after the price (50 required - some themes have lots of newlines around the price)
                        // INFO: the regex avoids returning all parent elements from the dom that contain the text
                        let priceElements = Util.findElementContainsRegex('*', new RegExp(`^.{0,50}${priceMoney}.{0,50}$`, 's')); // IMPORTANT: s flag to include new lines

                        // NEW: filter hidden price elements (allows to trigger amountNoDecimals search when no price el was found)
                        if(priceElements.length > 0){
                            priceElements = priceElements.filter(priceEl => {
                                let include = !Util.isHidden(priceEl) && !this._wrapper.contains(priceEl);
                                // NEW: b2b app blocks - exclude price elements inside the blocks
                                if(include && window.Cloudlift.b2b){
                                    if(priceEl.closest('[data-b2b-bulk]') || priceEl.closest('[data-b2b-discount]')){
                                        include = false;
                                    }
                                }
                                return include;
                            });
                        }

                        if(priceElements.length === 0){
                            log.debug('no productPrice elements found for price:', priceMoney);

                            if(ThemeConfig.amountNoDecimals){
                                priceMoney = formatMoney(variantPrice, '{{ amount_no_decimals }}');

                                log.debug('trying fallback {{ amount_no_decimals }} price: ', priceMoney);

                                priceElements = Util.findElementContainsRegex('*', new RegExp(`^.{0,50}${priceMoney}.{0,50}$`, 's')); // IMPORTANT: s flag to include new lines

                                // IMPORTANT: filter again
                                if(priceElements.length > 0){
                                    priceElements = priceElements.filter(priceEl => !Util.isHidden(priceEl) && !this._wrapper.contains(priceEl));
                                }
                            }
                        }

                        // select most specific match element
                        if(priceElements.length){

                            log.debug('found ' + priceElements.length + ' productPrice elements', priceElements);

                            // NEW: update all found prices
                            if(ThemeConfig.productPriceAll){

                                // filter to only update the deepest level child element
                                this._priceElList =  priceElements.filter(priceEl => {
                                    for (let i = 0; i < priceElements.length; i++) {
                                        const priceElement = priceElements[i];
                                        if(priceElement !== priceEl){ // exclude it self
                                            if(priceEl.contains(priceElement)){
                                                return false;
                                            }
                                        }
                                    }
                                    return true;
                                });
                            }
                            // DEFAULT: best match
                            else {
                                let bestMatch = null;
                                let bestDistance = -1;

                                // rect for absolute x/y distance calc
                                let elementRect = this._wrapper.getBoundingClientRect();

                                for (let i = 0; i < priceElements.length; i++) {
                                    const priceElement = priceElements[i];
                                    // DEPRECATED: exclude filter is already executed above: priceElements.filter(priceEl => !Util.isHidden(priceEl) && !this._wrapper.contains(priceEl));
                                    // exclude hidden
                                    // if(!Util.isHidden(priceElement)){
                                    // exclude element inside the options (other option prices)
                                    //if(!this._wrapper.contains(priceElement)){

                                    let priceRect = priceElement.getBoundingClientRect();

                                    // calc x/y distance to the options element
                                    let distance = Math.abs(elementRect.top - priceRect.top) + Math.abs(elementRect.left - priceRect.left);

                                    // first match
                                    // OR children from this match to get to the most specific element
                                    // OR smaller distance to find the closest price - required for featured products with same prices
                                    if(!bestMatch || bestMatch.contains(priceElement) || distance < bestDistance){
                                        bestMatch = priceElement;
                                        bestDistance = distance;
                                    }
                                    //}
                                    // }
                                }

                                if(bestMatch){
                                    // auto resolved always only one price
                                    this._priceElList = [ bestMatch ];
                                }
                            }


                        }

                    }
                }

                const updatePrice = () => {

                    // update price including price options (in micros)
                    let priceOptions = (this.price.total * 100);
                    let price = variantPrice + priceOptions;

                    // add-on products in micros (included in priceOptions)
                    let priceVariants = (this.price.variantTotal * 100);

                    let priceFormat = formatMoney(price, ThemeCurrency.moneyFormat); // NEW: most themes only use moneyFormat without currency

                    if(this._priceElList && this._priceElList.length){
                        for (let i = 0; i < this._priceElList.length; i++) {
                            const priceEl = this._priceElList[i];
                            priceEl.innerHTML = priceFormat;

                            // NEW: store price format in data attribute to detect outside changes
                            if(ThemeConfig.productPriceObserver){

                                // NEW: we compare textContent (auto removes HTML & custom HTML entities like &; etc.)
                                // INFO: if priceObserver is applied - the HTML is removed (was also the case with the DEPRECATED solution)
                                priceEl.dataset.liveFormat = priceEl.textContent;

                                /* DEPRECATED:  to avoid endless loop when price is updated and the browser fixes the HTML attributes we need to strip the HTML
                                if(priceFormat.indexOf('<') !== -1){
                                    // strip html from price format
                                    priceFormat = priceFormat.replace(/<[^>]*>?/gm, '');
                                    this._priceObserverHTML = true; // flag that html needs to be stripped
                                }

                                priceEl.dataset.liveFormat = priceFormat; */
                            }
                        }
                    }

                    // NEW: price compare support (selector only - no automatic lookup)
                    if (this._variantCompareAtPrice) {
                        // for simplicity we always lookup the price compare locations
                        this._priceCompareElList = document.querySelectorAll('[data-live-price-compare="' + this.settings.product.id + '"]');

                        if (this._priceCompareElList.length) {
                            let priceCompare = 0;
                            // NEW: percentage based compare at price in relation to original variant price
                            if (ThemeConfig.productPriceComparePct && variantPrice && variantPrice > 0) {

                                // calculate percentage: (compareAtPrice - basePrice) / basePrice * 100
                                const comparePercentage = ((this._variantCompareAtPrice - variantPrice) / variantPrice) * 100;

                                // formula: (variantPrice + optionsTotal) * (1 + percentage/100)
                                let currentTotalPrice = variantPrice + priceOptions;
                                priceCompare = Math.round(currentTotalPrice * (1 + comparePercentage / 100));

                            } else {
                                // absolute compare at price (variant compare at price + options total)
                                priceCompare = this._variantCompareAtPrice + priceOptions;
                            }

                            let priceCompareFormat = formatMoney(priceCompare, ThemeCurrency.moneyFormat); // NEW: most themes only use moneyFormat without currency
                            for (let i = 0; i < this._priceCompareElList.length; i++) {
                                const priceEl = this._priceCompareElList[i];
                                priceEl.innerHTML = priceCompareFormat;
                                priceEl.dataset.liveFormat = priceCompareFormat;
                            }
                        }
                    } else {
                        // clear stale references when variant has no compare-at price
                        this._priceCompareElList = null;
                    }

                    // trigger event to allow custom price UI updates                                       // NEW: options (used in b2b app)
                    this._triggerEvent('options:price', { price: price, priceFormat: priceFormat, options: priceOptions - priceVariants, variants: priceVariants });
                };

                updatePrice();

                // execute the price update again after a timeout (to prevent other apps like "conversion-bear-money" to overwrite it on variant changes)
                if(ThemeConfig.productPriceTimeout){
                    setTimeout(() => updatePrice(), 1000);
                }

                // NEW: register price observer to detect when a theme removes the element from the dom (e.g Dawn variant update)
                if(ThemeConfig.productPriceObserver && !this._priceElObserver){
                    if('MutationObserver' in window){
                        this._priceElObserver = new MutationObserver((mutations) => {

                            if(this._priceElList && this._priceElList.length){

                                log.trace('observer priceEl for removed nodes', this._priceElList);

                                let refresh = false;

                                for (let i = 0; i < mutations.length; i++) {
                                    const mutation = mutations[i];

                                    // only if nodes were removed
                                    if(mutation.removedNodes.length > 0){

                                        // exclude non element type changes (for speed improvement)    NEW: process text nodes (required to detect outside innerHTML changes)
                                        if(mutation.removedNodes.length === 1 && (mutation.removedNodes[0].nodeType !== 1 && mutation.removedNodes[0].nodeType !== 3)){
                                            continue;
                                        }

                                        // nodelist to array to execute checks
                                        const nodes = Array.from(mutation.removedNodes);

                                        // check if any price element was removed
                                        for (let j = 0; j < this._priceElList.length; j++) {

                                            const priceEl = this._priceElList[j];

                                            // NEW: compare direct text update with the previously set priceFormat attribute
                                            if(mutation.target === priceEl && priceEl.dataset.liveFormat){

                                                // NEW: compare text only
                                                let priceFormat = priceEl.textContent;
                                                /* DEPRECATED: strip html from price format to only compare the value
                                                 let priceFormat = priceEl.innerHTML;
                                                if(this._priceObserverHTML){
                                                    priceFormat = priceFormat.replace(/<[^>]*>?/gm, '');
                                                } */
                                                // only if changed (our own updates also trigger this event)
                                                if(priceEl.dataset.liveFormat !== priceFormat){
                                                    log.debug('priceEl was modified (reset to liveFormat):', priceEl);
                                                    // NEW: set text only
                                                    priceEl.innerText = priceEl.dataset.liveFormat;
                                                    // DEPRECATED: priceEl.innerHTML = priceEl.dataset.liveFormat;

                                                    // no refresh required (we just reset the content)
                                                }
                                            }
                                            // child || parent removed
                                            else if (nodes.indexOf(priceEl) > -1 || nodes.some(parent => parent.contains(priceEl))) {
                                                log.debug('priceEl was removed:', priceEl);
                                                refresh = true;
                                                break;
                                            }

                                        }

                                        // NEW: check if any compare price element was modified or removed
                                        if(!refresh && this._priceCompareElList && this._priceCompareElList.length){
                                            for (let k = 0; k < this._priceCompareElList.length; k++) {
                                                const compareEl = this._priceCompareElList[k];

                                                // NEW: compare direct text update with the previously set priceFormat attribute
                                                if(mutation.target === compareEl && compareEl.dataset.liveFormat){
                                                    // NEW: compare text only
                                                    let priceFormat = compareEl.textContent;
                                                    // only if changed (our own updates also trigger this event)
                                                    if(compareEl.dataset.liveFormat !== priceFormat){
                                                        log.debug('priceCompareEl was modified (reset to liveFormat):', compareEl);
                                                        // NEW: set text only
                                                        compareEl.innerText = compareEl.dataset.liveFormat;
                                                        // no refresh required (we just reset the content)
                                                    }
                                                }
                                                // child || parent removed
                                                else if (nodes.indexOf(compareEl) > -1 || nodes.some(parent => parent.contains(compareEl))) {
                                                    log.debug('priceCompareEl was removed:', compareEl);
                                                    refresh = true;
                                                    break;
                                                }
                                            }
                                        }

                                    }

                                    if(refresh){
                                        break;
                                    }
                                }

                                if(refresh){
                                    log.debug('-> triggering patchProductTotal');
                                    this._patchProductPriceTotal();
                                }

                            }

                        });

                        // INFO: we have to watch the full body (quite inefficient) because we cannot know which parent could be removed
                        this._priceElObserver.observe(document.body,{
                            // docs: https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver/observe
                            subtree: true,
                            childList: true
                        });
                    }
                }

            }
        }


    }

    _patchProductWeightTotal(){
        // INFO: currently only used on editor mode - but could also be shown on the product detail page
        if(this.price.weight !== 0 || (this._weightElList && this._weightElList.length)) {
            if (!this._weightElList || this._weightElList.length === 0) {
                // specific selector with product id, // data-live-weight="{{ product.id }}"
                this._weightElList = document.querySelectorAll('[data-live-weight="' + this.settings.product.id + '"]');
            }

            if(this._weightElList && this._weightElList.length){
                for (let i = 0; i < this._weightElList.length; i++) {
                    const weightEl = this._weightElList[i];

                    let weight = this.price.weight;

                    // total weight update requires selected variant weight
                    if(this._variantId) {
                        let variant = this._getVariantById(this._variantId);

                        if (variant && variant.weight) {
                            // product.json api return grams field
                            if(variant.grams) {
                                weight += (variant.grams / 1000);
                            }
                            // product.js api returns weight in grams without weight_unit
                            else { // default no "weight_unit" is grams
                                weight += (variant.weight / 1000);
                            }
                        }
                    }

                    // default kg
                    let weightFormat = weight.toFixed(2) + 'kg';
                    // custom weight unit via attribute data-live-weight-unit="g"
                    let weightUnit = weightEl.dataset.liveWeightUnit || 'kg';

                    if(weightUnit === 'g'){
                        weightFormat = Math.round(weight * 1000) + 'g';
                    } else if(weightUnit === 'lb'){
                        weightFormat = (weight * 2.205).toFixed(2) + 'lb';
                    } else if(weightUnit === 'oz'){
                        weightFormat = (weight * 35.274).toFixed(2) + 'oz';
                    }

                    weightEl.innerHTML = weightFormat;
                }
            }
        }
    }

    _patchProductSku(){
        if(this.price.sku.length || (this._skuElList && this._skuElList.length)) {

            // add variant as first sku part
            if(this._variantId) {
                let variant = this._getVariantById(this._variantId);
                if(variant && variant.sku){
                    this.price.sku.unshift(variant.sku);
                }
            }

            // generate sku string
            this.sku = this.price.sku.join(this.settings.skuSep);

            if(!this._skuElList || this._skuElList.length === 0){
                // specific selector with product id, // data-live-sku="{{ product.id }}"
                this._skuElList = document.querySelectorAll('[data-live-sku="'+this.settings.product.id+'"]');
            }

            if(this._skuElList && this._skuElList.length){
                for (let i = 0; i < this._skuElList.length; i++) {
                    const skuEl = this._skuElList[i];
                    skuEl.innerHTML = this.sku;
                }
            }
        }
    }

    _evaluateOptionValue(options){
        // only available with the formula extension
        if(AppExtensions.FORMULA) {

            for (let i = 0; i < options.length; i++) {

                const option = options[i];

                // custom option type for value calculations
                if (option.type === 'value-formula' && option.params.valueFormula) {

                    // avoid executing calculation if not visible - on the very first render visible is not
                    // evaluated yet (conditions run after values): only skip evaluated or conditional options
                    if(!option.visible && (option.visible !== undefined || option.conditions?.length)){
                        continue;
                    }

                    // hacky: we have to reset quantity cache (only done in _evaluateOptionPrice
                    if(this._pf?.qty){
                        this._pf.qty = undefined;
                    }

                    // create temp optionValue to execute the priceFormula logic (optionValue cached to also cache parsed function)
                    option._optionValue = option._optionValue || { value: '', priceFormula: option.params.valueFormula };

                    // assign the value (we can return any value type)
                    option.value = this._priceValueFormula(option, option._optionValue, false);

                    // optional copy value to another option
                    if(option.params.valueCopy){
                        let optionCopy = this._getOption(option.params.valueCopy);
                        if(optionCopy){
                            optionCopy.value = option.value;
                        }
                    }
                }

                // nested
                if (option.options && option.options.length) {
                    this._evaluateOptionValue(option.options);
                }
            }
        }
    }

    _evaluateOptionPriceTotal(options){

        for (let i = 0; i < options.length; i++) {

            const option = options[i];

            // only calculate if shown
            if(option.visible || ThemeConfig.evaluateOptionPriceHidden){ // NEW: hidden setting to evaluate price even if hidden by conditions (root only)

                // reset option priceText (shown in UI)
                if(option._priceText){
                    delete option._priceText;
                }

                // NEW: option value undefined check instead of option.value (required to also calculate priceTypes if the option does not yet have a value)
                if(typeof option.value !== 'undefined'){
                    // check if the selected value has a price
                    if(option.values){
                        for (let j = 0; j < option.values.length; j++) {
                            const optionValue = option.values[j];

                            let selected;

                            // NEW: check if controller has custom isSelected method (used for multiSelect)
                            if(option._controller && typeof option._controller.isSelected === 'function'){
                                selected = option._controller.isSelected(option, optionValue);
                            } else {
                                // DEFAULT: find selected value,  string comparison to avoid type differences & NEW: trim to avoid white space issues
                                selected = String(optionValue.value).trim() === String(option.value).trim();

                                // NEW: text with price (auto select as value is the input text)
                                if(optionValue.priceText){
                                    selected = !!option.value; // true/false flag if contains text
                                }

                                // NEW: upload with price (auto select if has uploaded files)
                                if(optionValue.priceUpload){
                                    selected = !!option.value;
                                }
                            }

                            // NEW: custom priceTypes price needs to be calculated before its selected (required to show on Ui)
                            if(optionValue.priceType) {
                                // store merchant entered price value (required to re-calculate prices)
                                if(!optionValue.hasOwnProperty('_price')){ // only init once
                                    optionValue._price = optionValue.price;
                                }

                                // INFO: we calculate the optionValue.price with the actual price

                                // PERCENTAGE of product price
                                if(optionValue.priceType === PriceType.PERCENTAGE){
                                    let variant = this._getVariantById(this._variantId);
                                    if(variant && variant.price){ // micros conversion / percentage
                                        optionValue.price = ((variant.price / 100) / 100) * optionValue._price;
                                    }  else {
                                        optionValue.price = 0;
                                    }
                                }
                                // LETTER
                                else if(optionValue.priceType === PriceType.LETTER){
                                    let text = option.value;
                                    // NEW: optional exclude letters (e.g. spaces)
                                    if(option.params.priceTextClean){
                                        // create regex if not already  SAME logic as textClean
                                        if(!option._priceTextClean){
                                            try {
                                                option._priceTextClean = new RegExp(option.params.priceTextClean, option.params.textCleanFlags || 'ug'); // default flags: always use unicode and global flags
                                            } catch (error){
                                                log.error('failed to compile priceTextClean pattern for option: ', option.params.priceTextClean, option, error);
                                            }
                                        }
                                        if(option._priceTextClean){
                                            // check if text contains pattern to clean
                                            if(option._priceTextClean.test(text)){
                                                text = text.replaceAll(option._priceTextClean, '');
                                            }
                                        }
                                    }
                                    let letters = text.length;
                                    // NEW: optional only apply price after X letter used
                                    if(option.params.priceTextMin){
                                        // subtract min letters
                                        letters = Math.max(0, letters - option.params.priceTextMin);
                                    }
                                    optionValue.price = optionValue._price * letters;
                                }
                                // FORMULA (only if extension is enabled)
                                else if(optionValue.priceType === PriceType.FORMULA && AppExtensions.FORMULA){
                                    optionValue.price = this._priceValueFormula(option, optionValue);
                                }
                                // UPLOAD - price per file
                                else if(optionValue.priceType === PriceType.UPLOAD){
                                    // Count uploaded files from comma-separated URLs
                                    let uploadCount = 0;
                                    if(option.value){
                                        let urls = option.value.split(',').filter(url => url.trim() !== '');
                                        uploadCount = urls.length;
                                    }
                                    // Apply priceUploadMin (only charge after N uploads)
                                    if(option.params.priceUploadMin){
                                        uploadCount = Math.max(0, uploadCount - option.params.priceUploadMin);
                                    }
                                    optionValue.price = optionValue._price * uploadCount;
                                }
                            }

                            if(optionValue.priceVariant && !optionValue.bundle){ // bundle product is handled separately

                                // NEW: priceVariantCart check if already in cart and avoid adding again by setting quantity to zero - needs to be checked before its selected (required to show on Ui)
                                if(optionValue.priceVariantCart && !this.settings.editorMode){  // editor no cart lookup
                                    // always check until quantity is set to zero (required when same product is added multiple times on the product page - we cannot set optionValue.priceVariantCart = false)
                                    if(optionValue.priceVariant.quantity !== 0){
                                        // INFO: we are in a sync loop - so just fire the lookup and update the quantity async
                                        this._lookupCartQuantity(optionValue.priceVariant.id).then((quantity) => {
                                            if(quantity > 0){
                                                optionValue.priceVariant.quantity = 0;
                                                optionValue.price = 0;
                                                // re-render to show updated price
                                                // INFO: not the best solution - if this causes issue with some options we need to check the cart quantity similar to _variantsInCurrency - but then also again AFTER add to cart
                                                this.render();
                                            }
                                        });
                                    }
                                }

                                // NEW: show price based on dynamically selected quantity (default backend UI sets .price = productVariant.price * fixed quantity)
                                // IMPORTANT:needs to be calculated before its selected (required to show on Ui)
                                if(optionValue.quantityInput && !isNaN(optionValue.priceVariant.quantity)){

                                    // store single product price value (required to re-calculate when quantity updates)  INFO: ThemeConfig.productLinkAvailable already sets the price to the current live price
                                    if(!optionValue.hasOwnProperty('_price')){ // only init once

                                        // IMPORTANT: no stock check the price could already be multiplied with the quantity by the backend UI
                                        if(!ThemeConfig.productLinkAvailable && optionValue.priceVariant.quantity > 1){
                                            optionValue.price = parseFloat(optionValue.priceVariant.price); // re-parse the linked product price
                                        }

                                        optionValue._price = optionValue.price;
                                    }

                                    optionValue.price = optionValue._price * optionValue.priceVariant.quantity;
                                }
                            }

                            if(selected){

                                // NEW: weight support (we check flag to allow disable and keep weight values in db)
                                if(this.settings.priceWeight && optionValue.weight){
                                    this.price.weight += optionValue.weight;
                                }

                                // NEW: sku support (we check flag to allow disable and keep sku values in db)
                                if(this.settings.sku && optionValue.sku){
                                    this.price.sku.push(optionValue.sku);
                                }

                                // BETA: allow excluding add-on in total (re-think when bundle addon is released)
                                let totalExclude = false;

                                // NEW: price that adds it's own product variant
                                if(optionValue.priceVariant) {

                                    // add product as bundle product (.bundle flag set by Bundle option)
                                    // no separate add to cart - cart transform expands the product in the cart
                                    // bundlePriceVariant: global setting adds ALL product variants to the bundle (no separate add to cart)
                                    // priceVariantBundlePrice: implies bundle for this value (custom price is only chargeable via cart transform)
                                    let bundle = AppExtensions.BUNDLE && (optionValue.bundle || optionValue.priceVariantBundlePrice || ThemeConfig.bundlePriceVariant);

                                    if(!bundle){
                                        // add to price (to add when adding to cart)
                                        this.price.variants.push(optionValue.priceVariant);

                                        // NEW: priceVariantQuantity sync with main product
                                        // INFO: bundle products are ALWAYS multiplied with the quantity (done by Shopify Cart transform)
                                        if(optionValue.priceVariantQuantity){
                                            // -1 triggers quantity lookup
                                            optionValue.priceVariant.quantity = -1;
                                        }
                                    } else {

                                        let bundleProduct = null;
                                        // bundleProduct config provided by BUNDLE extension
                                        if(typeof optionValue.bundle === 'object'){
                                            bundleProduct = optionValue.bundle;

                                            // HACKY: main product price update discount - save to update total price
                                            if(bundleProduct.id === '0'){
                                                this.price.bundleMain = bundleProduct.price;
                                            }
                                        }
                                        // default bundle product for add-ons via ThemeConfig.bundlePriceVariant
                                        else if(optionValue.priceVariant.quantity > 0) { // ONCE per oder can set quantity to zero (exclude)
                                            bundleProduct = {
                                                id: optionValue.priceVariant.id,
                                                quantity: optionValue.priceVariant.quantity,
                                                handle: optionValue.priceVariant.handle, // used to lookup product in cart and render bundle layout
                                            }
                                            // IMPORTANT: product price always required
                                            // Shopify function cannot fetch it when ExpandOperation is used - ID input would need to be added for all bundle products
                                            // optionValue.price already converted in this._variantsInCurrency
                                            // optionValue._price contains single quantity price (required - shopify does the multiply)
                                            bundleProduct.price = (optionValue._price || optionValue.price);
                                        }

                                        if(bundleProduct){
                                            // store base price to allow currency change during checkout
                                            if(ThemeCurrency.currencyFactor !== 1){
                                                bundleProduct.priceBase = Math.round(bundleProduct.price / ThemeCurrency.currencyFactor);
                                            }
                                            /* if(ThemeCurrency.checkout.key){ Validation skipped (rust not supported yet)
                                                bundleProduct.key = this._pk(bundleProduct.price, ThemeCurrency.checkout.key);
                                            } */
                                            this.price.bundle.push(bundleProduct);
                                        }
                                    }

                                    // BETA: flag on priceVariant (no config yet)   || manual theme setting
                                    totalExclude = optionValue.priceVariant.exclude || ThemeConfig.productVariantExcludeTotal;

                                    // add variant total to subtract
                                    if(optionValue.price && !totalExclude){
                                        this.price.variantTotal += optionValue.price;
                                        // same calc on readonly price (the price variable can still be edited in the debugger - but the variable needs to be edited for every option)
                                        this._pr = this._pp({ ...this._pr, variantTotal: this._pr.variantTotal + optionValue.price });
                                    }
                                }

                                if(optionValue.price){

                                    let priceOption;

                                    let priceValue = optionValue.price;

                                    // options price in base currency (only set if currency is converted)
                                    let priceBase;

                                    // external product variants already converted in this._variantsInCurrency
                                    if(!optionValue.priceVariant) {
                                        // NEW: always store base value (used when currency is changed - and cart transform always uses base price)
                                        priceBase = priceValue; // save priceBase to store on option
                                        this.price.totalBase += priceBase;
                                        // same calc on readonly price (the price variable can still be edited in the debugger - but the variable needs to be edited for every option)
                                        this._pr = this._pp({ ...this._pr, totalBase: this._pr.totalBase + priceBase });

                                        // NEW: dynamic currency conversion
                                        if (ThemeCurrency.currencyConvert) {
                                            // update price value in current currency
                                            priceValue = currencyConvert(priceBase);
                                        }
                                    }

                                    // NEW: multi select option could already have a priceOption
                                    if(this.price.options.hasOwnProperty(option.name)){
                                        priceOption = this.price.options[option.name];

                                        // add combined option price
                                        priceOption.price += priceValue;

                                        if(priceOption.priceBase){
                                            priceOption.priceBase += priceBase;
                                        }
                                    } else {
                                        priceOption = {
                                            option: option,
                                            price: priceValue,
                                            priceBase: priceBase // undefined if not multi currency
                                        };
                                    }

                                    // total add each individual optionValue price (converted base prices are added once as totalBase after the loop)
                                    if(!totalExclude && !(priceBase !== undefined && ThemeCurrency.currencyConvert)){
                                        this.price.total += priceValue;
                                        // same calc on readonly price (the price variable can still be edited in the debugger - but the variable needs to be edited for every option)
                                        this._pr = this._pp({ ...this._pr, total: this._pr.total + priceValue });
                                    }

                                    //  price text with the combined priceOption price (multi select support)
                                    priceOption.priceText = formatMoney(priceOption.price * 100, ThemeCurrency.moneyFormat);

                                    // show price on label if enabled
                                    if(option.label && option.params.labelPriceText){
                                        // label supports html wrap in span for styling support
                                        option._priceText = priceOption.priceText;
                                    }

                                    // NEW: avoid price option entry if price is from separate product
                                    if(!optionValue.priceVariant){
                                        this.price.options[option.name] = priceOption;
                                    }
                                }
                            }

                        }
                    }
                }

                // nested
                if(option.options && option.options.length){
                    this._evaluateOptionPriceTotal(option.options);
                }
            }

        }
    }

    _priceValueFormula(option, optionValue, forceNumber = true){

        let priceValue = 0;
        // NEW: the price formula state may not exist yet on the very first render - value formulas
        //      without conditions derive before _evaluateOptionPrice initializes it (same defaults)
        this._pf = this._pf || { qty: undefined, usedQty: false, divideByQty: false };
        this._pf.divideByQty = false;

        let priceFormula = optionValue.priceFormula;
        try {
            // only parse price function once - INFO: backend editor overwrites the optionValue which automatically clears the parsed function
            if(!optionValue._priceFunction) {
                try {
                    // expression only (no variable declaration & no return)
                    if(priceFormula.indexOf('return') === -1){
                        priceFormula = 'return ( ' + priceFormula + ' )';
                    }
                    // execute as function instead of eval: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/eval#never_use_eval!
                    optionValue._priceFunction = Function('"use strict";return (function(option, value, price, options){ ' + priceFormula + ' })')();
                } catch (error){
                    log.error('failed to parse optionValue.priceFormula: ', optionValue, priceFormula, error);
                    if(this.settings.editorMode){
                        option.errors = option.errors || [];
                        if(option.errors.indexOf(error.message) === -1) {
                            option.errors.push(error.message);
                        }
                    }
                }
            }

            if(optionValue._priceFunction){
                // execute with current option and optionValue entered price (util initialized via lazy getter)
                priceValue = optionValue._priceFunction(option, optionValue.value, optionValue._price, this._priceFunctionUtil);

                // NEW: default force a number (disabled when used as value formula that can return anything)
                if(forceNumber){

                    // no result set to 0
                    if(!priceValue){
                        priceValue = 0;
                    }
                    // NEW: force number conversion (e.g. when returning a string)
                    else if(typeof priceValue !== 'number'){

                        priceValue = parseFloat(priceValue);

                        // check for NaN
                        if(isNaN(priceValue)){
                            priceValue = 0;
                        }
                    }

                    // NEW: auto-divide by quantity to get per-unit price
                    // when quantity(true) was called, the formula result is a total
                    if(this._pf.divideByQty && this._pf.qty > 1){
                        priceValue = priceValue / this._pf.qty;
                    }

                    // NEW: auto deduct the variant price from the calculated price (only used in dimension option)
                    // BETA: could also use global flag productPriceDeductVP
                    if(priceValue > 0 && option.params.priceFormulaDeductVP){
                        priceValue = this._dVP(priceValue);
                    }
                }

            }

        } catch (error){
            log.error('failed to calculate price with priceFormula: ', priceFormula, error);
            if(this.settings.editorMode){
                option.errors = option.errors || [];
                if(option.errors.indexOf(error.message) === -1){
                    option.errors.push(error.message);
                }

            }
        }

        return priceValue;
    }

    get _priceFunctionUtil() {
        const util = {
            option: (name) => this._getOption(name),
            value: (name) => {
                const o = this._getOption(name);
                return o ? o.value : '';
            },
            price: (name) => {
                // INFO: only works if the price is already calculated
                if(this.price.options.hasOwnProperty(name)){
                    let priceOption = this.price.options[name];
                    // IMPORTANT: always use base price for calculations if provided (multi currency)
                    return priceOption.priceBase || priceOption.price;
                }
                return 0;
            },
            // INFO: we use a function to avoid exposing internal _controller reference in priceFormula (maybe we change it in the future)
            controller: (name) => {
                const o = this._getOption(name);
                return o && o._controller ? o._controller : {};
            },
            variant: (id) => {
                // custom id or current selection
                return this._getVariantById(id || this._variantId);
            },
            weight: (name, weight, value) => {
                const o = this._getOption(name);
                if(o){
                    for (let i = 0; i < o.values.length; i++) {
                        const optionValue = o.values[i];
                        // DEFAULT: set the weight on the current value (allows dimension option to set the weight on itself)
                        if(optionValue.value == (value || o.value)){
                            // update own weight
                            if(!isNaN(weight)){
                                optionValue.weight = weight;
                            }
                            return optionValue.weight;
                        }
                    }
                }
                return 0;
            },
            error: (name, message) => {
                // INFO: we cannot reference the current option as the "option" variable is lost is scope - so we always have to provide the name
                const o = this._getOption(name);
                if(o) {
                    o.errors = o.errors || [];
                    // optional translation support
                    message = Translate.translate(message);
                    // only add once
                    if (o.errors.indexOf(message) === -1) {
                        o.errors.push(message);
                    }
                    // HACKY: flag to keep error when validating on add to cart
                    o._errorFormula = true;
                }
            },
            list: (name) => {

                // resolve name to identifier
                if(this.settings.lists.hasOwnProperty('_name')) { // contains mapping
                    let nameMap = this.settings.lists._name;
                    let identifier;
                    for (const nameValue of nameMap) {
                        if(nameValue.text === name){
                            identifier = nameValue.value;
                            break;
                        }
                    }

                    if(identifier){
                        // if found > return values defined in list
                        if (this.settings.lists.hasOwnProperty(identifier)) {
                            // AVOID deep copy (executed on every change - list refs outside of formula already have a deep copy)
                            // JSON.parse(JSON.stringify(this.settings.lists[identifier]));
                            const listValues = this.settings.lists[identifier];
                            // JSON list edge case - return nested data as object
                            if(listValues.length === 1 && listValues[0].hasOwnProperty('data')){
                                return listValues[0].data;
                            }
                            return listValues;
                        }
                    }
                }

                return [];
            },
            lookup: (values, x, y, text) => {

                // INFO: when no Y provided the lookup is for the area (x = area)
                // INFO: allow to pass text as second param (optional)
                if(typeof y === 'string'){
                    text = y;
                    y = null;
                }

                // loop backwards
                for (let i = values.length - 1; i >= 0; i--) {
                    let optionValue = values[i];

                    let valueX = parseFloat(optionValue.value);

                    if(x >= valueX){

                        // optional text key - has to match (allows to have options in the price list)
                        if(text && optionValue.text !== text){
                            continue;
                        }

                        // price table > Y ranges nested
                        if(y && optionValue.values?.length){

                            for (let j = optionValue.values.length - 1; j >= 0; j--) {
                                let optionValueY = optionValue.values[j];

                                let valueY = parseFloat(optionValueY.value);

                                if(y >= valueY){
                                    log.info('[live-options] price list lookup:', x, y, 'match:', valueX, valueY, 'price: ', optionValueY.price);
                                    // return matched price
                                    return optionValueY.price;
                                }
                            }
                        } else {
                            log.info('[live-options] price list lookup:', x, 'match:', valueX, 'price:', optionValue.price);
                            // return matched price
                            return optionValue.price;
                        }
                    }
                }

                log.warn('[live-options] price list lookup:', x, 'no match');

                return 0;
            },
            quantity: (total) => {
                // track that quantity was used in a formula
                this._pf.usedQty = true;

                // when total=true, the formula result is a total price
                // that should be divided by quantity to get per-unit price
                if(total === true){
                    this._pf.divideByQty = true;
                }

                // return cached value if already resolved in this evaluation cycle
                if(this._pf.qty !== undefined){
                    return this._pf.qty;
                }

                let qty;

                // first try from option type 'quantity' (if exists)
                const qOption = this._getOption('quantity');
                if(qOption?.type === 'quantity' && qOption._controller){
                    qty = qOption._controller.quantity;
                } else {
                    // default from input[name="quantity"] (theme or form)
                    let quantityEl = this.shopifyStore.findProductFormQuantityEl((this._productFormParentEl || this._productForm));
                    qty = quantityEl ? (parseInt(quantityEl.value) || 1) : 1;
                }

                this._pf.qty = qty;
                return qty;
            }
        };
        // Replace getter with data property on first access (subsequent reads skip the getter)
        Object.defineProperty(this, '_priceFunctionUtil', { value: util, writable: true });
        return util;
    }

    _watchFormSubmit(){

        // prevent double form event execution ( important also to prevent loop when triggered via _triggerFormEvent )
        this._productFormSubmit = false;

        // NEW: manual add to cart support (used for sticky and when the theme does not save options)
        this._handleCartAdd();

        // NEW: provide custom parent if defined
        this.shopifyStore.onProductFormSubmit(this._productForm, this._productFormParentEl || this._productForm, (e) => this._handleFormSubmit(e), ThemeConfig.productFormCapture);

        // NEW: prevent form submit when pressing enter in text fields (annoying on mobile)
        (this._productFormParentEl || this._productForm).addEventListener('keypress', (e) => {
            if(e.target && e.target.tagName === 'INPUT'){ // INFO: textarea enter creates newline so we do not have to apply the check
                let key = e.keyCode || e.which;
                // enter key
                if (key === 13) {
                    e.preventDefault(); // prevent submit
                    e.target.blur(); // remove focus (close keyboard on mobile)
                }
            }
        });

        // NEW: extend form.checkValidity with app validation (e.g. Horizon gates its add-to-cart animation on it)
        if(ThemeConfig.productFormValidityPatch){
            const nativeCheckValidity = this._productForm.checkValidity.bind(this._productForm);
            // a prevented click reports invalid, the re-trigger ( _productFormSubmit ) valid > the theme animates once, when the item is added
            this._productForm.checkValidity = () => nativeCheckValidity() && this.validate() && (this._productFormSubmit || !this._preventsFormSubmit());
        }

    }

    // true when the add to cart is cancelled and re-triggered after the async work ( same conditions as preventSubmit in _handleFormSubmit )
    _preventsFormSubmit(){
        const priceReadonly = this._pr || { total: 0 };
        return this._hasExportPreviews() || priceReadonly.total !== 0 || this.price.variants.length > 0 || !!this._itemKey
            || (!!AppExtensions.AI && Object.keys(this._getAiUploads() || {}).length > 0);
    }

    _handleFormSubmit(event) {
        const sync = event && event.detail && event.detail.sync;
        const runAsync = !sync;
        // NEW: capture for a save for later ( _captureCartProperties ) - validation, exports and properties like the add to cart, no price add-ons and no cart
        // INFO: never touches the submit lock or the button - a save clicked during a prevented add to cart exports alongside it
        const capture = !!(event && event.detail && event.detail.capture);

        if(this._productFormSubmit && runAsync && !capture){
            return Promise.resolve(false);
        }

        // NEW: a save for later runs - an add to cart meanwhile is cancelled, the shopper submits again after the save
        if(this._cartAddBlocked && runAsync && !capture){
            event.preventDefault();
            event.stopImmediatePropagation();
            return Promise.resolve(false);
        }

        if(runAsync){
            if(!capture){
                this._productFormSubmit = true;
            }

            if(!this.validate()){
                // reset submit flag
                if(!capture){
                    this._productFormSubmit = false;
                }
                // cancel submit
                event.preventDefault();
                event.stopImmediatePropagation();
                return;
            }

            // NEW: experimental to exclude all options that have no value ( restored on options:submit, which a capture does not emit )
            if(ThemeConfig.productOptionsEmptyExclude && !capture){
                this._disableEmptyOptions();
            }
        }

        // set price readonly to local variable (only used to obfuscate the variable names in dev tools)
        const priceReadonly = this._pr || { total: 0, totalBase: 0, variantTotal: 0 };

        // preview with export config
        let exportPreview = this._hasExportPreviews();
        // add price options product to add the additional option price || RARE case free product variants
        let hasPriceOptions = priceReadonly.total !== 0 || this.price.variants.length > 0;
        // ai generated img need to be uploaded to our own storage
        if(sync){
            exportPreview = false;
            this._aiUploads = undefined;
        } else {
            this._aiUploads = AppExtensions.AI ? this._getAiUploads() : undefined;
            if(!exportPreview && this._aiUploads && Object.keys(this._aiUploads).length > 0){
                exportPreview = true; // force export
            }
        }

        // TODO temp force all buy now to use manual checkout (until fixed) - ONLY required when not prevented anyways (e.g. exportPreview / add-ons) - DAWN seems fixed - but we leave it for now
        this._buyNowCapture = true;

        // NEW: store interactive preview locations for restore support
        if(ThemeConfig.previewState){
            this._savePreviewState();
        }

        let preventSubmit = exportPreview || hasPriceOptions || this._itemKey || (this._buyNowCapture && this.shopifyStore.isExpressPaymentButton(event.target)); // NEW: express button handling

        // custom code on form submit required ( a capture has no submit to cancel, no button and no loader of the add to cart )
        if(runAsync && preventSubmit && !capture) {

            // cancel submit ( browser default for submit buttons )
            event.preventDefault();

            // prevents further propagation of the current event in the capturing and bubbling phases.
            // allows other handlers on the same element to be executed
            // event.stopPropagation()

            // If several listeners are attached to the same element for the same event type, they are called in the order in which they were added.
            // If stopImmediatePropagation() is invoked during one such call, no remaining listeners will be called.
            // first listener wins
            event.stopImmediatePropagation();

            // store event to re-trigger after preview is exported
            this._productFormEvent = event;

            // NEW: disable button to avoid double submit on slow exports
            this._triggerFormDisabled(true);

            // NEW: submit prevent event (e.g. to show a custom loader/message)
            this._triggerEvent('options:submit_prevent');
        }

        let submitPromises = [];

        if(exportPreview){

            // NEW: single export promise (which is sequentially executed)
            let exportPromise = Promise.resolve();

            // NEW: multi preview export support
            let previews = this._getPreviews();

            // HACKY: fake preview entry to trigger the export logic for the AI uploads (before exports)
            if(AppExtensions.AI && Object.keys(this._aiUploads).length > 0){
                previews = [{previewConfig: { ai: true }}, ...previews];
            }

            for (let i = 0; i < previews.length; i++) {
                const preview = previews[i];

                // check again if this preview has export enabled
                if(this._isExportPreview(preview)){
                    // each preview gets it's own progress bar
                    let progressEl = document.createElement('progress');
                    progressEl.max = 100;
                    progressEl.value = 0;
                    progressEl.className = 'cl-po--preview-progress';

                    let progressWrapperEl = this._wrapper;

                    // NEW: show progress in popup element
                    if(this._popupEl){
                        progressWrapperEl = this._popupEl.querySelector('.options-atc');
                    }

                    progressWrapperEl.appendChild(progressEl);

                    let rejectDelay = (reason) => {
                        return new Promise(function(resolve, reject) {
                            setTimeout(reject.bind(null, reason), 1000); // one second re-try delay
                        });
                    }

                    let logSuccess = false;

                    // sequentially export (required to avoid memory issues on mobile devices)
                    exportPromise = exportPromise.then(() => {

                        // reject error pattern for promise based retry
                        let exportPreviewPromise = Promise.reject();

                        // retry 3 times
                        for(let i = 0; i < 3; i++) {

                            exportPreviewPromise = exportPreviewPromise.catch((error) => {

                                if(error){

                                    logSuccess = true; // flag to log error > success case

                                    log.warn('[live-options] preview export error', error);

                                    // add retry param to find in logs
                                    if(i > 1){
                                        error.try = i;
                                    }

                                    // log to server (async ignore result)
                                    ThemeConfig.previewExportLog && this._upload._fileMeta.exportFailed(error);
                                }

                                try {
                                    // return promise to chain
                                    return this._exportPreview(preview, progressEl);
                                } catch (err) { // sync error (export)
                                    err.sync = true; // flag for logging
                                    // continue retry again with error
                                    return Promise.reject(err);
                                }

                            })
                            // retry after delay
                            .catch(rejectDelay);
                        }

                        // failed or completed > remove loader
                        exportPreviewPromise.finally(() => {
                            progressWrapperEl.removeChild(progressEl);
                        });

                        return exportPreviewPromise;

                    }).then(() => {

                        // if previous error was logged > log success
                        if(logSuccess){
                            this._upload._fileMeta.exportFailed({ success: true });
                        }

                    }).catch((error) => {

                        if(error){
                            log.error('[live-options] preview final failed', error);

                            error.failed = true; // flag for logging

                            ThemeConfig.previewExportLog && this._upload._fileMeta.exportFailed(error);

                            // upload contentLength error > force retry of export (avoid empty links..)
                            if(error.upload){
                                alert(error.error);
                                ThemeConfig.previewExportRequired = true;
                            }
                        }

                        // clear stale preview form properties to avoid submitting old links
                        let exportConfig = preview.previewConfig?.exportConfig;
                        if(exportConfig){
                            if(exportConfig.thumbnailProperty){
                                this._removeFormProperty(exportConfig.thumbnailProperty);
                            }
                            if(exportConfig.originalProperty){
                                this._removeFormProperty(exportConfig.originalProperty);
                            }
                        }

                        // DEFAULT: continue with add to cart

                        // CUSTOM: prevent add to cart when export failed
                        if(ThemeConfig.previewExportRequired){
                            this._productFormSubmit = false; // next button click executes the preview export again
                            // fails without any alert
                            return Promise.reject();
                        }
                    });
                }
            }

            // add export promise to submit to wait for the last export to finish
            submitPromises.push(exportPromise);
        }

        if(runAsync && hasPriceOptions && !capture){
            // IMPORTANT: all cart modifications have to be executed in serial order
            let cartAddPromise = Promise.resolve();

            // additional product variants that have to be added to the cart
            if(this.price.variants.length){

                for (let i = 0; i < this.price.variants.length; i++) {
                    const priceVariant = this.price.variants[i];

                    // store id to allow validation (currently not used)
                    let properties = {};

                    // NEW: once per cart sets quantity to 0 = skip adding
                    if(priceVariant.quantity === 0){
                        continue;
                    }

                    // set the quantity based on the main product quantity (quantity -1 is set in _evaluateOptionPriceTotal)
                    if(priceVariant.quantity === -1){
                        priceVariant.quantity = this._productFormQuantity();
                    }

                    let priceVariantItem = this.shopifyCart.toItem(priceVariant.id, priceVariant.quantity, properties);

                    // add to cart and chain promise
                    cartAddPromise = cartAddPromise.then(() => this.shopifyCart.addItem(priceVariantItem)).catch(error => {
                        let msg = 'Live Product Options: '+(error.description || '')+'\n**please check the linked products on options**';
                        // show message to let merchant know linked products have an issue (avoid alert in case product is out of stock or accidentally removed)
                        this.shopifyStore.isThemeEditor() ? alert(msg) : console.error(msg);
                    });
                }
            }

            // wait on all cart add to finish
            submitPromises.push(cartAddPromise);

        }

        let optionsId = null;

        if (hasPriceOptions) {
            optionsId = CartOptionsPrice.generateId();

            const totalReadonly = this._pp({
                t: priceReadonly.total - priceReadonly.variantTotal
            });

            if (totalReadonly.t > 0 || totalReadonly.t < 0 && ThemeCurrency.checkout.negative) {

                const optionsPriceReadonly = this._pp({
                    m: Math.round(totalReadonly.t * 100),
                    b: Math.round(priceReadonly.totalBase * 100)
                });

                let properties = {};

                properties[CartItemProperties.ID] = optionsId;

                let jsonPrice = false;

                if (ThemeCurrency.checkout.json !== false && !ThemeCurrency.checkout.update && ThemeCurrency.checkout.variantId) {
                    jsonPrice = {};
                }

                for (const optionName in this.price.options) {
                    if (this.price.options.hasOwnProperty(optionName)) {
                        const priceOption = this.price.options[optionName];
                        let priceText = priceOption.priceText.replace(/(<([^>]+)>)/gi, '');
                        properties[priceOption.option.name] = priceText;
                        if (priceOption.priceBase) {
                            properties['_' + priceOption.option.name] = priceOption.priceBase;

                            if (jsonPrice) {
                                jsonPrice[priceOption.option.name] = priceOption.priceBase;
                            }
                        }
                    }
                }

                this._setFormProperty(CartItemProperties.ID, optionsId);
                this._setFormProperty(CartItemProperties.PRICE, optionsPriceReadonly.m);

                if (ThemeCurrency.currencyConvert) {
                    this._setFormProperty(CartItemProperties.CURRENCY, ThemeCurrency.currency);
                    this._setFormProperty(CartItemProperties.PRICE_BASE, optionsPriceReadonly.b);
                }

                if (ThemeCurrency.checkout.validate && ThemeCurrency.checkout.key) {
                    this._setFormProperty(CartItemProperties.PRICE_KEY, this._pk(
                        ThemeCurrency.currencyConvert ? optionsPriceReadonly.b : optionsPriceReadonly.m, ThemeCurrency.checkout.key));
                }

                CartOptionsPrice.storeItem(optionsId, {properties, currency: ThemeCurrency.currency});

                if (jsonPrice) {
                    this._setFormProperty(CartItemProperties.JSON_PRICE, JSON.stringify(jsonPrice));
                }

            } else {
                hasPriceOptions = false;
            }
        }

        if (!hasPriceOptions) {
            this._removeFormProperty(CartItemProperties.ID);
            this._removeFormProperty(CartItemProperties.PRICE);
            this._removeFormProperty(CartItemProperties.PRICE_BASE);
            this._removeFormProperty(CartItemProperties.PRICE_KEY);
            this._removeFormProperty(CartItemProperties.CURRENCY);
            this._removeFormProperty(CartItemProperties.JSON);
            this._removeFormProperty(CartItemProperties.JSON_PRICE);
        }

        if (this.settings.priceWeight && this.price.weight > 0) {
            this._setFormProperty(CartItemProperties.WEIGHT, this.price.weight)
        } else {
            this._removeFormProperty(CartItemProperties.WEIGHT);
        }

        if (this.settings.sku && this.sku) {
            this._setFormProperty(this.settings.skuProp, this.sku)
        } else {
            this._removeFormProperty(this.settings.skuProp);
        }

        if (this.price.bundle.length) {
            this._setFormProperty(CartItemProperties.BUNDLE, JSON.stringify(this.price.bundle));
        } else {
            this._removeFormProperty(CartItemProperties.BUNDLE);
        }

        const syncResult = {hasPriceOptions, optionsId};

        if (sync) {
            return syncResult;
        }

        // IMPORTANT: always execute preventSubmit logic when internal event ( a capture resolves the properties from there )
        if(!preventSubmit && (event.type === 'cloudlift.cart' || capture)){
            preventSubmit = true;
        }

        // NEW: store all form options as single json property (required for cart transform "expand" to preserve line item properties)
        let propertiesAsJson = null;  // ONLY required if the cart transform is applied - price or image update  // NEW: bundle always requires to save json of options

        // NEW: shopify admin can show parent properties (but still missing on line_item.properties on order data)
        if(ThemeCurrency.checkout.json !== false){ // default to true for backwards compatibility

            if(!ThemeCurrency.checkout.update && (hasPriceOptions || ThemeCurrency.checkout.image && exportPreview) || this.price.bundle.length){
                propertiesAsJson = () => {
                    let formProperties = this.shopifyStore.getFormProperties(this._productForm);
                    // avoid re-adding it json property itself (next submission)
                    let propertyBlackList = [CartItemProperties.JSON, CartItemProperties.JSON_PRICE, CartItemProperties.PRICE_KEY, CartItemProperties.BUNDLE, CartItemProperties.BUNDLE_TITLE];
                    for (const property of propertyBlackList) {
                        delete formProperties[property];
                    }

                    this._setFormProperty(CartItemProperties.JSON, JSON.stringify(formProperties));
                };
            }
        }


        // only if submit was prevented
        if(preventSubmit){

            // wait for all submit handlers to finish
            return Promise.all(submitPromises)
                .then(() => {

                    // IMPORTANT: we have to wait for all promises to finish (export preview adds new properties)
                    if(propertiesAsJson){
                        propertiesAsJson();
                    }

                    // NEW: capture > the line as the add to cart would send it, nothing is added or updated
                    if(capture){
                        return this.shopifyStore.getFormProperties(this._productForm);
                    }

                    // check if item was restored from the cart && VARIANT has not changed (update does not support variant change)
                    if(this._itemKey && this._itemKey.indexOf(this._variantId) !== -1){

                        // update item
                        const updateItem = () => {
                            return this.shopifyCart.updateItemById({
                                id: this._itemKey,
                                quantity: this.shopifyStore.findProductFormQuantity((this._productFormParentEl || this._productForm)), // support quantity updates
                                properties: this.shopifyStore.getFormProperties(this._productForm) // parse all custom properties defined in the form (from this app and potential other apps)
                            }).then(() => {

                                // NEW: global event to update cart UI specific for a theme (useful for cart drawer)
                                if(typeof window.clOnCartUpdate === 'function'){
                                    window.clOnCartUpdate();
                                } else {
                                    // we don't know how the theme is updated so we have to navigate to the cart
                                    window.location.href = this.shopifyStore.settings.api + '/cart'; // api prefix for locale
                                }
                            });
                        }

                        if(ThemeConfig.productItemModal){

                            let dialogPrefix = 'product.options.item.modal.';

                            let dialogEl = document.createElement('dialog');
                            dialogEl.className = 'cl-po--dialog';
                            dialogEl.innerHTML =`
                              <h4>${Translate.get(dialogPrefix + 'title')}</h4>
                              <div class="action">
                                <button type="button" class="cl-po--swatch cl-po--swatch-button cl-po--small" value="update">${Translate.get(dialogPrefix + 'update')}</button>
                                <button type="button" class="cl-po--swatch cl-po--swatch-button cl-po--small" value="add">${Translate.get(dialogPrefix + 'add')}</button>
                              </div>`;

                            // the dialog sits inside the product form > a form submit would reach the theme, which can cancel it (Horizon)
                            dialogEl.querySelectorAll('button').forEach(btn => btn.addEventListener('click', () => dialogEl.close(btn.value)));

                            // polyfill requires manual register
                            if(window.dialogPolyfill !== undefined){
                                window.dialogPolyfill.registerDialog(dialogEl);
                                // safari height fix
                                dialogEl.style.height = 'auto';
                            }

                            // add wrapper (required to call showModal & scoped skin CSS to work)
                            this._wrapper.appendChild(dialogEl);

                            dialogEl.showModal();

                            dialogEl.addEventListener('close', () => {
                                if(dialogEl.returnValue === 'update'){ // INFO: esc key is returnValue = ''
                                    updateItem();
                                } else {
                                    // NEW: manual add to cart handling - re-trigger form to add (we are in a promise here that cannot continue below)
                                    if(event.type === 'cloudlift.cart'){
                                        delete this._itemKey;
                                        delete this._productFormSubmit;
                                        this._handleFormSubmit(event); // NOT optimal (builds preview again - but OK as this case is very rare)
                                    } else {
                                        // trigger original form event (to support ajax cart)
                                        this._triggerFormEvent();
                                    }
                                }
                            });

                            // avoid adding triggering add to cart
                            return true;
                        }
                        // config if update by default when item is set
                        else if(ThemeConfig.productItemUpdate){
                            return updateItem();
                        }

                    }

                    // NEW: buy now logic for price add-ons with buy now express buttons
                    if(ThemeConfig.productBuyNow && (event.target instanceof Node && event.target !== document)
                        && this.shopifyStore.isExpressPaymentButton(event.target)){

                        // overwrite event to force manual add to cart > and then go to checkout
                        event = new CustomEvent('cloudlift.cart', { detail: {
                                onSuccess: () => {
                                    // manually go to checkout
                                    window.location.href = this.shopifyStore.settings.api + '/checkout'; // api prefix for locale;
                                }
                            }, cancelable: true, bubbles: false });
                    }

                    // NEW: manual cart add support
                    if(event.type === 'cloudlift.cart'){

                        // manually add item based on current form
                        return this.shopifyCart.addItem({
                            id: this._variantId,
                            quantity: this._productFormQuantity(),
                            properties: this.shopifyStore.getFormProperties(this._productForm) // parse all custom properties defined in the form (from this app and potential other apps)
                        }).then(() => {

                            // IMPORTANT: reset submit flag (browser back button restored page state where _productFormSubmit is kept to true)
                            this._productFormSubmit = false;

                            if(event.detail && typeof event.detail.onSuccess === 'function'){
                                event.detail.onSuccess();
                            } else {
                                // default to cart page
                                window.location.href = this.shopifyStore.settings.api + '/cart'; // api prefix for locale
                            }

                            return true;
                        });

                    }

                    // NEW (hacky): outside submit event trigger (does not add to cart) - only succcess case solved for now.
                    if(event.type === 'cloudlift.submit'){

                        // reset state
                        this._triggerFormDisabled(false);
                        this._productFormSubmit = false;

                        if(event.detail && typeof event.detail.onSuccess === 'function'){
                            event.detail.onSuccess();
                        }

                    } else {

                        // DEFAULT: trigger original form event (to support ajax cart)
                        this._triggerFormEvent();
                    }

                    // NEW: submit event trigger (used on personalize option to close after successful submit)
                    this._triggerEvent('options:submit');

                    return true;

                }).catch((error) => {
                    log.error('[live-options] submit handler error', error);

                    // quantity validation - show alert
                    if(error.status === 422 && error.description){
                        alert(error.description);
                    }

                    // a capture holds neither the lock nor the button
                    if(!capture){
                        this._productFormSubmit = false;

                        this._triggerFormDisabled(false);
                    }

                    return false;
                });

        } else {

            // IMPORTANT: no price add-ons and no export BUT can still have custom options
            if(propertiesAsJson){
                propertiesAsJson();
            }

            // reset submit flag - we use timeout to avoid double validation ( event is triggered on add to cart click & submit to support different themes )
            setTimeout(() => {
                // NEW: submit event trigger (also trigger when not prevented)
                this._triggerEvent('options:submit');

                this._productFormSubmit = false;

                this._triggerFormDisabled(false);

                this._clearOptionValues();

                // NEW (hacky): outside submit event trigger (does not add to cart) - only succcess case solved for now.
                if(event?.type === 'cloudlift.submit') {
                    if (event.detail && typeof event.detail.onSuccess === 'function') {
                        event.detail.onSuccess();
                    }
                }

            }, 300);
        }
        // not executed
        return Promise.resolve(false);
    }

    _handleCartAdd(){
        // find all add to cart element on page
        let addToCarBtnList = document.querySelectorAll('[data-live-cart-add]');
        if(addToCarBtnList.length){
            log.debug('found manual cart add btn', addToCarBtnList);

            for (let i = 0; i < addToCarBtnList.length; i++) {
                const addBtnEl = addToCarBtnList[i];

                // IMPORTANT: disable capture of normal add to cart to prefer this one (avoid executing _handleFormSubmit twice and adding linked products twice)
                // IMPORTANT: only disable if this add button is inside the form (avoid disable if only the sticky cart button is used here)
                if((this._productFormParentEl || this._productForm).contains(addBtnEl)){
                    ThemeConfig.productFormCapture = false;
                }

                addBtnEl.addEventListener('click', (event) => {

                    // optional product id match (required for featured products)
                    if(addBtnEl.dataset.liveCartAdd){
                        if(this.settings.product.id.toString() !== addBtnEl.dataset.liveCartAdd){
                            return;
                        }
                    }

                    // cancel event
                    event.preventDefault();
                    event.stopImmediatePropagation();
                    // IMPORTANT: reset in case the submit event was triggered before
                    this._productFormSubmit = false;
                    // trigger add to cart manually with our own add to cart logic
                    this._handleFormSubmit(new CustomEvent('cloudlift.cart', { detail: {}, cancelable: true, bubbles: false }));
                }, { capture: true });
            }
        }
    }

    _setFormProperty(property, value){

        let inputEl;

        if(!this._formInputs){
            this._formInputs = {};
        } else if(this._formInputs.hasOwnProperty(property)) {
            inputEl = this._formInputs[property];
        }

        if(!inputEl){
            inputEl = document.createElement('input');
            inputEl.type = 'hidden';
            inputEl.name = 'properties[' + property + ']';
            inputEl.value = value;

            // only add once
            this._productForm.appendChild(inputEl);
        } else {
            inputEl.value = value;
        }

        // store reference for potential updates
        this._formInputs[property] = inputEl;
    }

    // NEW: quantity of the line ( with support for app blocks ) - a quantity input of the personalize popup wins when above 1
    _productFormQuantity(){
        let qty = this.shopifyStore.findProductFormQuantity(this._productFormParentEl || this._productForm);

        // NEW: qty input support in personalize popup (quantity discount or popup with quantity input)
        if(this._popupEl){
            let qtyPopup = this.shopifyStore.findProductFormQuantity(this._popupEl);
            if(qtyPopup > 1){
                qty = qtyPopup;
            }
        }

        return qty;
    }

    // NEW: the cart line properties as the add to cart builds them, the previews exported and the AI images uploaded ( capture submit: no price add-on, nothing goes to the cart )
    // resolves null when an option is not valid ( e.g. an upload still running - the options show their errors ), rejects when a required export failed
    _captureCartProperties(){
        return Promise.resolve(this._handleFormSubmit(new CustomEvent('cloudlift.capture', {
            detail: { capture: true },
            cancelable: true,
            bubbles: false
        }))).then((properties) => {
            if(properties === false){
                throw new Error('cart properties not captured');
            }
            return properties || null;
        });
    }

    // NEW: a save for later runs > an add to cart submitted meanwhile is cancelled ( see _handleFormSubmit ), the button stays enabled
    _blockCartAdd(blocked){
        this._cartAddBlocked = blocked;
    }

    // NEW: the cart line properties as the add to cart builds them for the current values ( sync submit: no export or cart side effects )
    _getCartProperties(){
        this._handleFormSubmit(new CustomEvent('cloudlift.ajax', {
            detail: { sync: true },
            cancelable: true,
            bubbles: false
        }));

        return this.shopifyStore.getFormProperties(this._productForm);
    }

    _removeFormProperty(property){
        if(this._formInputs && this._formInputs.hasOwnProperty(property)) {
            let formInput = this._formInputs[property];
            formInput.parentNode.removeChild(formInput);
            delete this._formInputs[property];
        }
    }

    _triggerFormEvent(){

        // make sure the button is enabled again
        this._triggerFormDisabled(false);

        // if there was a product form event re-trigger
        if(this._productFormEvent){
            let e = this._productFormEvent;
            // re trigger the source event manually
            if(e.target || e.currentTarget){
                // click on add to cart
                if(e.type === 'click'){
                    // IMPORTANT: first check target (this is what fired the event)
                    if(e.target && e.target.click){
                        e.target.click();
                    }
                    // fallback to currentTarget to get the element where the handler is executed instead of the child of the button
                    else if(e.currentTarget && e.currentTarget.click){
                        e.currentTarget.click();
                    }
                    // fallback if parent is button (nested svg that did not work with manual click event)
                    else if(e.target.parentNode.tagName === 'BUTTON'){
                        // manually trigger click
                        e.target.parentNode.click();
                    }
                }
                // submit event
                else if(e.type === 'submit'){
                    e.target.submit();
                }
            } else {
                log.error('missing event target - manual form submit');
                this._productForm.submit();
            }
            this._productFormEvent = null;

            // NEW: avoid double submit within delay time - required when other apps also trigger the add to cart button
            if(ThemeConfig.productFormSubmitDelay){
                setTimeout(() => {
                    this._productFormSubmit = false;
                }, (window.clSubmitDelay || 1000)); // default: reset after 1 second
            } else {
                this._productFormSubmit = false;
            }
        }

        // NEW: options clear support
        this._clearOptionValues();
    }

    _clearOptionValues(){
        // only if saved on init
        if(this._optionValuesClear){
            setTimeout(() => {
                // reset initial values
                this.setValues(this._optionValuesClear);
                // update UI
                this.render();
                this._renderPreview();
                // IMPORTANT: if the options are cleared - we have to trigger a render of the popup template (to reset the values - we moved the personalize group out of the form)
                if(this._popupEl){
                    this._renderPopup(this._personalizeOption);
                }
            }, 1000);
        }
    }

    _triggerFormDisabled(disabled){

        if(ThemeConfig.productFormDisabled){

            if(disabled){

                // find button from stored event
                if(this._productFormEvent){

                    let target = (this._productFormEvent.target || this._productFormEvent.currentTarget);

                    if(target){

                        if(target.tagName === 'BUTTON'){
                            this._btnDisabled = target;
                        } else {
                            // find button in form (window has no closest)
                            this._btnDisabled = target.closest?.('button');
                        }
                        // if found disable button
                        if(this._btnDisabled){
                            this._btnDisabled.setAttribute('disabled', 'disabled');
                        }
                    }

                }

            } else if (this._btnDisabled){
                this._btnDisabled.removeAttribute('disabled');
                delete this._btnDisabled;
            }
        }

    }

    // default params to support nested
    _getControllers(controllers = {}, options = this.config.options){

        for (let i = 0; i < options.length; i++) {
            const option = options[i];

            // nested
            if(option.options && option.options.length){
                // we pass controllers reference
                this._getControllers(controllers, option.options);
            }
            // top level or nested option with controller
            if(option._controller) {
                // NEW: store controller with type to avoid wrong controller if type changed
                controllers[option.name + '_' + option.type] = option._controller;
            }
        }

        return controllers;
    }

    _setControllers(controllers, options = this.config.options){
        // set option controllers for
        for (let i = 0; i < options.length; i++) {
            const option = options[i];

            // nested
            if(option.options && option.options.length){
                // we pass controllers reference
                this._setControllers(controllers, option.options);
            }
            // top level or nested option with controller
            if(controllers.hasOwnProperty(option.name + '_' + option.type)) {
                // NEW: with type
                option._controller = controllers[option.name + '_' + option.type];
            }
        }
    }

    _getOption(name, options = this.config.options){
        // set option controllers for
        for (let i = 0; i < options.length; i++) {
            const option = options[i];

            if(option.name === name){
                return option;
            }

            // nested
            if(option.options && option.options.length){
                let optionChild = this._getOption(name, option.options);
                // only return if found
                if(optionChild){
                    return optionChild;
                }
            }
        }

        return null;
    }

    _initPreview(){

        if(this.config.previewConfig.gallery){

            this._gallery = new ProductGallery(this.config, this._productForm, this.conditionsEngine,  {
                api: this.settings.api, // map urls
                assets: this.settings.assets,
                selector: this.settings.selectorPreview,
                selectorThumbnail: this.settings.selectorThumbnail,
                position: this.settings.positionPreview,
                sticky: this.settings.stickyPreview,
                stickyMobile: this.settings.stickyPreviewMobile,
                stickyMobileSize: this.settings.stickyPreviewMobileSize,
                zoom: this.settings.previewZoom,
                maxHeight: this.settings.maxHeightPreview,
                product: this.settings.product,
                variantId: this._variantId, // current variant id ( update via events )
                editorMode: this.settings.editorMode,
                galleryConfig: this.config.previewConfig.galleryConfig // gallery config 1:1 mapping
            });

            // make sure ready promise is available (can be missing when gallery is not found)
            if(this._gallery.ready){

                const gallery = this._gallery;

                gallery.ready.then((result) => {
                    // gallery replaced (editor re-init) before it was ready > ignore
                    if(gallery !== this._gallery){
                        return;
                    }

                    // set active preview
                    this._preview = gallery._preview;

                    // active preview changes - listener on the final element (_init can re-point it to the parent)
                    gallery.element.addEventListener('preview:active', () => {
                        if(gallery === this._gallery){
                            this._preview = gallery._preview;
                        }
                    });

                    // render with the real previews (options that list the gallery views)
                    this.render();
                });

            }

        } else {
            // init with main previewConfig
            this._preview = new ProductPreview(this.config, this.config.previewConfig, this._productForm, this.conditionsEngine,  {
                api: this.settings.api, // map urls
                assets: this.settings.assets,
                selector: this.settings.selectorPreview,
                selectorThumbnail: this.settings.selectorThumbnail,
                position: this.settings.positionPreview,
                sticky: this.settings.stickyPreview,
                stickyMobile: this.settings.stickyPreviewMobile,
                stickyMobileSize: this.settings.stickyPreviewMobileSize,
                zoom: this.settings.previewZoom,
                maxHeight: this.settings.maxHeightPreview,
                product: this.settings.product,
                variantId: this._variantId, // current variant id ( update via events )
                editorMode: this.settings.editorMode
            });
        }

        // Themes without a preview/gallery container have no element to observe.
        const previewElement = (this._preview || this._gallery)?.element;
        if(!previewElement){
            return;
        }

        // Layer actions tooltip event handlers
        previewElement.addEventListener('layer:action:delete', (e) => {
            const ctx = this._resolveControllerByLayer(e.detail);
            ctx?.controller?.onRemove?.(ctx.option);
        });
        previewElement.addEventListener('layer:action:duplicate', (e) => {
            const ctx = this._resolveControllerByLayer(e.detail);
            ctx?.controller?.onDuplicate?.(ctx.option);
        });

        previewElement.addEventListener('layer:action:edit', (e) => {
            const ctx = this._resolveControllerByLayer(e.detail);
            if (!ctx) return;

            // Upload-Lift replacement needs the layer's file index, even when live crop is enabled.
            // multi-select instances share the template controller (no own file index) > focusOption below
            // (older Upload-Lift builds have no field.replaceFile > normal file input below)
            if (ctx.option.type === 'upload-lift' && ctx.option._controller?.field?.replaceFile && !ctx.option._isMultiSelectInstance) {
                ctx.option._controller.replace(e.detail).catch(error => log.error('Image replacement failed', error));
                return;
            }

            // Try controller method first (group-editor has focusOption)
            if (ctx.controller?.focusOption) {
                ctx.controller.focusOption(ctx.option);
                return;
            }

            const layer = e.detail;

            // Fallback for non-group-editor options: file upload or focus input
            const optionEl = document.querySelector(`[data-option="${ctx.option.name}"]`);
            if (optionEl) {
                if (layer.type.includes('upload')) {
                    let uploadBtn = optionEl.querySelector(`input[type=file]`);
                    uploadBtn?.click();
                } else {
                    optionEl.scrollIntoView({behavior: 'smooth', block: 'center'});
                    const input = optionEl.querySelector('input, textarea, select');
                    if (input) {
                        setTimeout(() => input.focus({preventScroll: true}), 300);
                    }
                }
            }
        });

    }

    // only used when values are changed outside of the normal option change event (e.g cart item restore, values clear)
    _renderPreview(){

        if(this.config.preview){
            // multi preview
            if(this._gallery && this._gallery.ready){
                // gallery is ready when all previews are ready
                return this._gallery.ready.then(() => Promise.all(this._gallery._previews.map(preview => preview.renderPreview())));
            }
            // single preview
            else if(this._preview && this._preview.ready){
                return this._preview.ready.then(() => this._preview.renderPreview())
            }
        }

        return Promise.resolve();
    }

    // backend editor
    _destroyPreview(){
        // gallery destroy
        if(this._gallery){
            this._gallery.destroy();
            // delete
            delete this._gallery;
        }
        // single preview
        else if(this._preview){
            this._preview.destroy();
        }

        // delete preview (gallery also stores main preview)
        delete this._preview;
    }

    _getPreviews(){
        // gallery OR single preview OR no preview
        return this._gallery ? this._gallery._previews : (this._preview ? [this._preview] : []);
    }

    _resolveControllerByLayer(layer) {
        if (!layer?.params?.option) return null;
        const option = this._getOption(layer.params.option);
        if (!option) return null;

        const controller = (layer.params.optionParent && this._getOption(layer.params.optionParent)?._controller)
            || option._controller;

        return { option, controller };
    }

    _previewStateProperty(preview) {
        const viewName = preview.previewConfig?.name;
        // single view > skip preview name (gallery property only on primary view) || switch from single to gallery - support restoring Main view
        const isSingleView = preview.previewConfig?.gallery === false || viewName === 'Main';

        const normalized = String(viewName || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');

        return isSingleView || !normalized ? '__v' : `__v_${normalized}`;
    }

    _savePreviewState() {
        const previews = this._getPreviews();

        for (let i = 0; i < previews.length; i++) {
            const preview = previews[i];
            const property = this._previewStateProperty(preview);

            if (!property || typeof preview.exportState !== 'function') {
                continue;
            }

            const state = preview.exportState();

            if (state) {
                this._setFormProperty(property, state);
            } else {
                this._removeFormProperty(property);
            }
        }
    }

    // queue the saved view states ( __v, __v_<view> ) on their previews - applied with the next preview render (cart item & saved configuration)
    async _queuePreviewStates(properties) {
        if(!ThemeConfig.previewState){
            return;
        }

        const previewStates = {};
        for (const property in properties) {
            if (!properties.hasOwnProperty(property)) {
                continue;
            }
            // match reserved preview state property
            if (PREVIEW_STATE.test(property)) {
                previewStates[property] = properties[property];
            }
        }
        // only if view needs to be restored
        if (Object.keys(previewStates).length > 0) {
            // wait for init to complete
            if (this._gallery?.ready) {
                await this._gallery.ready;
            } else if (this._preview?.ready) {
                await this._preview.ready;
            }

            const previews = this._getPreviews();

            for (let j = 0; j < previews.length; j++) {
                const preview = previews[j];
                const property = this._previewStateProperty(preview);

                if (property && previewStates[property]) {
                    preview.queueRestoreState(previewStates[property]);
                }
            }
        }
    }

    _isExportPreview(preview){
        // export enabled & at least thumbnail or original active  || NEW: custom exports
        let isExport = preview.previewConfig.export && (preview.previewConfig.exportConfig.thumbnail || preview.previewConfig.exportConfig.original || (AppExtensions.EXPORT && preview.previewConfig.exports && preview.previewConfig.exports.length));

        // fake preview to export AI images to our own storage
        if(AppExtensions.AI && preview.previewConfig.ai){
            return true;
        }

        if(!isExport){
            // NEW: crop needs export
            for (let j = 0; j < preview.previewConfig.layers.length; j++) {
                const layer = preview.previewConfig.layers[j];
                if(layer.params.crop && layer.type === 'upload'){
                    return true;
                }
                // NEW: layer coords export
                if(layer.params.exportCoords){
                    return true;
                }
            }

        }

        return isExport;
    }

    _hasExportPreviews(){

        // check if any preview has export
        let previews = this._getPreviews();

        for (let i = 0; i < previews.length; i++) {
            if(this._isExportPreview(previews[i])){
                return true;
            }
        }

        return false;
    }

    _getAiUploads(options = this.config.options, values = {}) {

        for (let i = 0; i < options.length; i++) {
            const option = options[i];

            if (option.type === 'ai') {
                if(option.value){
                    values[option.value] = {
                        name: option.name, // name used to store in properties
                        url: option.value
                    };
                }
            }

            // nested
            if (option.options && option.options.length) {
                this._getAiUploads(option.options, values);
            }
        }

        return values;
    }

    async _uploadPreview(fileMeta, progressEl, maxProgress = 100) {
        let files = await this._upload.upload([fileMeta], (progress) => {

            if (progressEl && progress.lengthComputable) {
                let value = ((progress.loaded / progress.total) * 100);
                // avoid more than maxProgress
                value = Math.min(value, maxProgress);

                // we set fake progress on preview export ( avoid setting below )
                if (value > progressEl.value) {
                    progressEl.value = value;
                }
            }
        });

        return files[0]; // single file upload
    }

    // the custom exports ( export extension ) of a view: a single SVG uploaded as basis for all its exports
    // resolves the link of each export by property, null when the view is not visible ( no export data )
    async _exportCustom(preview, progressEl){

        let previewConfig = preview.previewConfig;

        // INFO: we upload a single SVG as basis for all exports
        let format = 'svg';

        // INFO: currently we also use excludeOriginal setting on layer - if we wanted to support settings for each export - we would need to upload a new SVGs for each export
        let paramExclude = 'excludeOriginal';

        let blob = !ThemeConfig.previewBlobs;

        // ai usage also requires blob = false to replace the upload urls
        if(blob && (AppExtensions.AI && Object.keys(this._aiUploads).length)){
            blob = false;
        }

        // full size SVG export
        let data = preview.export({
            width: previewConfig.width,
            height: previewConfig.height
        }, false, blob, paramExclude, format);

        // IMPORTANT: exclude empty data (preview not visible)
        if(data){

            // IMPORTANT: also replace AI uploads with our own storage (avoid links to external services in export)
            if (AppExtensions.AI && this._aiUploads && Object.keys(this._aiUploads).length) {
                data = await this._exportAi(data);
                // INFO: takes precedence over blob export
            }
            // NEW: hacky way to upload embedded blobs (currently only required for removeBg filter) we upload the blob to get an URL
            // INFO: below the upload layer is actually uploaded again - but we don't want to re-order the logic just for this edge case
            else if(ThemeConfig.previewBlobs){
                data = await this._exportBlobs(data);
            }

            let fileMeta = this._upload._fileMeta.fromBlob(data, 'preview_export.' + format);
            fileMeta.meta.variant = 'export';

            // fake progress based on export original upload that follows
            let maxProgress = previewConfig.exportConfig.original ? 50 : 80;

            let file = await this._uploadPreview(fileMeta, progressEl, maxProgress);

            // NEW: export variant can have multiple exports
            const urls = {};
            for (let j = 0; j < previewConfig.exports.length; j++) {
                const exportConfig = previewConfig.exports[j];
                // NEW: an entry with a print area takes its size and crop from the area visible for this order
                const url = this._exportConfigUrl(exportConfig, file, preview.areas);
                if (url) {
                    urls[exportConfig.property] = url;
                }
            }
            return urls;
        }

        return null;
    }

    // NEW: test export of the app editor - the custom exports of one view ( position ) without validation, form or cart
    // exports: the view's export entries to use ( optional, the editor's unsaved ones ) - resolves like _exportCustom
    async _exportView(position, exports){
        // the app editor only, never on a storefront
        if(!this.settings.editorMode){
            return null;
        }
        const preview = this._getPreviews().find(p => p.previewConfig?.position === position);
        if(!preview){
            return null;
        }
        if(exports){
            preview.previewConfig.exports = exports;
        }
        // as the add to cart: AI images are moved to our own storage inside the exported SVG
        this._aiUploads = AppExtensions.AI ? this._getAiUploads() : undefined;
        return this._exportCustom(preview, null);
    }

    async _exportPreview(preview, progressEl){

        let previewConfig = preview.previewConfig;

        // additional check ( export could only include the crop image )
        if(previewConfig.export){

            progressEl.value = 15; // fake some progress

            // NEW: changed order to go from least to most ram intensive export (svg, thumbnail, original)

            // NEW: custom export extension
            if (AppExtensions.EXPORT && previewConfig.exports && previewConfig.exports.length) {
                const urls = await this._exportCustom(preview, progressEl);
                for (let j = 0; j < previewConfig.exports.length; j++) {
                    const property = previewConfig.exports[j].property;
                    if(urls && urls[property]){ // a print area entry without a visible area gets no link
                        this._setFormProperty(property, urls[property]);
                    } else {
                        // RESET (could be set from previous product on same page)
                        this._removeFormProperty(property);
                    }
                }
            }

            if (previewConfig.exportConfig.thumbnail) {

                // hardcoded lower quality 500x
                // NEW: png instead of jpeg (gets bigger output but supports transparency via masks - clipMask is black otherwise even with canvas.backgroundColor white)
                let data = preview.export({
                    width: previewConfig.exportConfig.thumbnailWidth,
                    height: previewConfig.exportConfig.thumbnailHeight
                }, false, true, 'excludeThumbnail','png', 0.8);

                // IMPORTANT: exclude empty data (preview not visible)
                if(data){
                    let fileMeta = this._upload._fileMeta.fromBlob(data, 'preview_thumbnail.png');
                    fileMeta.meta.variant = 'thumbnail';

                    let fileMetaThumbnail = null;

                    // NEW: support to save the active view as main preview (not yet configurable in app ui - hence why we have the global window.clExportPreviewActive = true; )
                    if(previewConfig.exportConfig.thumbnailActive || window.clExportPreviewActive){

                        // always use the fixed _preview if active
                        fileMeta.meta.property = preview.active ? CartItemProperties.PREVIEW : previewConfig.exportConfig.thumbnailProperty;

                        // avoid saving if not active when thumbnailActiveOnly=true
                        previewConfig.exportConfig.thumbnailActiveOnly = true; // TODO REMOVE when exportConfig is configurable in app
                        if((!previewConfig.exportConfig.thumbnailActiveOnly) || preview.active){
                            fileMetaThumbnail = fileMeta;
                        }

                    } else {
                        fileMeta.meta.property = previewConfig.exportConfig.thumbnailProperty; // set on form after upload
                        fileMetaThumbnail = fileMeta;
                    }

                    if(fileMetaThumbnail){

                        // fake progress based on export original upload that follows
                        let maxProgress = previewConfig.exportConfig.original ? 50 : 80;

                        // NEW: shopify preview upload TODO draft - only enable if useful on checkout - currently not useful as checkout ads cdn suffix
                        let shopifyUpload = false;

                        if(!shopifyUpload){
                            let file = await this._uploadPreview(fileMetaThumbnail,  progressEl,maxProgress);

                            this._setFormProperty(file.meta.property, file.url);

                        } else {

                            // HACKY: add to cart with file upload attachment - remove again to let theme handle the add to cart
                            let response = await this.shopifyCart.addItemFormData({
                                id: this._variantId,
                                properties: {
                                    'upload': fileMetaThumbnail._file // upload file blob
                                }
                            }, (progress) => {

                                progressEl.value = maxProgress;
                            });

                            let lineItem = response.data;

                            // on success directly remove from cart
                            await this.shopifyCart.removeItemById(lineItem.key)

                            // store the upload link on the property
                            this._setFormProperty(fileMeta.meta.property, lineItem.properties.upload);

                        }
                    }

                } else {
                    this._removeFormProperty(previewConfig.exportConfig.thumbnailProperty);
                }
            }

            if (previewConfig.exportConfig.original) {

                // NEW: crop the original export
                let cropExport = previewConfig.exportConfig.crop && previewConfig.exportConfig.cropConfig.width
                    ? previewConfig.exportConfig.cropConfig : false;

                // NEW: format settings (default png to avoid breaking existing config) - empty could mean auto detect based if masks are used
                let format = previewConfig.exportConfig.originalFormat || 'png';

                // full size png export
                let data = preview.export({
                    width: previewConfig.width,
                    height: previewConfig.height
                }, cropExport, true, 'excludeOriginal', format);

                // IMPORTANT: exclude empty data (preview not visible)
                if(data){
                    let fileMeta = this._upload._fileMeta.fromBlob(data, 'preview_original.' + format);
                    fileMeta.meta.variant = 'original';
                    fileMeta.meta.property = previewConfig.exportConfig.originalProperty; // set on form after upload

                    let file = await this._uploadPreview(fileMeta, progressEl);

                    this._setFormProperty(file.meta.property, file.url);

                } else {
                    this._removeFormProperty(previewConfig.exportConfig.originalProperty);
                }

            }
        }

        // NEW: AI image upload
        if(preview.previewConfig.ai){
            for (const url in this._aiUploads) {
                if (this._aiUploads.hasOwnProperty(url)) {

                    let aiUpload = this._aiUploads[url];

                    let response = await fetch(url)
                    let blob = await response.blob();

                    let fileMetaAi = this._upload._fileMeta.fromBlob(blob, aiUpload.name + '.' + blob.type.replace('image/', ''));
                    fileMetaAi.meta.variant = 'ai';

                    let fileBlob = await this._uploadPreview(fileMetaAi, progressEl);

                    // store uploaded url
                    aiUpload.url = fileBlob.url;

                    let uploadUrl = aiUpload.url;
                    let aiOptionName = aiUpload.name;
                    // store property (overwrite the the actual option value with the uploaded url)
                    this._setFormProperty(aiOptionName, uploadUrl);

                    // check if AI option linked with upload option > also update the upload option value
                    let aiOption = this._getOption(aiOptionName);
                    if(aiOption && aiOption.params.optionUpload){
                        this._setFormProperty(aiOption.params.optionUpload, uploadUrl);
                    }
                    // upscale support
                    if(aiOption.params.aiUpscale){
                        // AI upscale export url
                        let upscaleUrl = this.settings.api + '/api/ai/image/export/' + fileBlob.path;
                        // update to also reference in the export extension
                        aiUpload.url = upscaleUrl;
                        // store as extra property (always hidden)
                        this._setFormProperty((aiOptionName.charAt(0) === '_' ? '' : '_') + aiOptionName + '_x', upscaleUrl);
                    }

                }
            }
            // no layers available
            return;
        }

        // NEW: upload layer with crop
        for (let j = 0; j < preview.previewConfig.layers.length; j++) {
            const layer = preview.previewConfig.layers[j];

            // crop OR background removed
            if(layer.type === 'upload' && (layer.params.crop || layer._removeBg)){

                if(layer.params.exportCropCoords){
                    // IMPROVEMENT: exportCoords are in relation to preview canvas - not the image upload
                    // preview.exportLayer does calculate the correct coordinates (needs to be returned somehow) to store here
                }

                // NEW: disable export of crop  (root no UI setting)
                if(layer.params.exportCrop === false){
                    continue; // keeps unedited link already stored on the option
                }
                // NEW: only export crop when changed by the customer (flag _interactive is set) (root no UI setting)
                if(layer.params.exportCropInteractive && !layer._interactive){
                    continue; // keeps unedited link already stored on the option
                }

                let format = 'jpeg';

                // only use png if input was png (jpeg much smaller output) || png force when background was removed
                if(layer._url && layer._url.indexOf('.png') !== -1 || layer._removeBg){
                    format = 'png';
                }

                let data = preview.exportLayer(layer.name, true, format, 0.8); // JPEG quality is almost the same but output is 10x smaller than with 1.0! (use Upload-Lift to save quality)

                // use the option name if set
                let name = layer.params.option || layer.name;

                // INFO: layer is only exported if currently rendered on the preview
                if(data){

                    let fileMeta = this._upload._fileMeta.fromBlob(data, name + '_crop.' + format);
                    fileMeta.meta.variant = 'original';
                    // INFO: this overwrites the upload file link (works by adding a new hidden input field with the same name as the upload option)
                    fileMeta.meta.property = name;

                    // NEW: removeBg avoid overwriting original image - better to save it separately (also includes the crop)
                    if(layer._removeBg){
                        fileMeta.meta.property = '_remove_bg_' + name;
                    }

                    let file = await this._uploadPreview(fileMeta, progressEl);

                    this._setFormProperty(file.meta.property, file.url);

                    // NEW: mark with preview & layer (the same uploda can be referenced on multiple views & layers)
                    this._formInputs[file.meta.property].dataset.layer = previewConfig.name + "_" + layer.name;

                } else {
                    // NEW: only remove if it was added by this exact layer (edge case two layers, black/white for  the same upload)
                    if (this._formInputs[name]?.dataset.layer === previewConfig.name + "_" + layer.name) {
                        this._removeFormProperty(name); // remove in case layer was not found or export returned empty (preview not visible)
                    }
                }

            }

            // NEW: export layer coords (sync not promise needed)
            if(layer.params.exportCoords){
                // we export the layer by name (because of child layers - to avoid nesting here - but less efficient )
                // for large number of layer exports it would be better to export a full json structure
                let coords = preview.exportLayerCoords(layer.name);
                if(coords){
                    // store layer coords as hidden property with the layer name (multi view could have layer name collision - OK for now)
                    this._setFormProperty('_' + layer.name, `x:${coords.x},y:${coords.y},w:${coords.w},h:${coords.h}` + (coords.r !== 0 ? `,r:${coords.r}` : ''));
                } else {
                    this._removeFormProperty('_' + layer.name); // remove in case layer was not found
                }
            }
        }
    }

    async _exportAi(svg){
        for (const url in this._aiUploads) {
            if (this._aiUploads.hasOwnProperty(url)) {
                const aiUrl = this._aiUploads[url].url;
                // replace previous url with uploaded url
                svg = svg.replace(new RegExp(url, 'g'), aiUrl);
            }
        }

        // create blob (required as we passed blob:false to export)
        return new Blob([svg], {type: 'image/svg+xml'});
    }

    async _exportBlobs(svg){
        //  extract all blob urls from from xlink:href=""  from svg
        let regex = /xlink:href="blob:([^"]+)"/g;
        // loop over all matches
        let match;
        while (match = regex.exec(svg)) {
            // only cdn.shopify.com have a size param
            const blobUrl = match[1];

            let url = `blob:${blobUrl}`;
            let response = await fetch(url)
            let blob = await response.blob();

            let fileMetaBlob = this._upload._fileMeta.fromBlob(blob, 'blob.' + blob.type.replace('image/', ''));
            fileMetaBlob.meta.variant = 'blob';

            let fileBlob = await this._uploadPreview(fileMetaBlob);

            svg = svg.replace(url, fileBlob.url);
        }

        // create blob (required as we passed blob:false to export)
        return new Blob([svg], {type: 'image/svg+xml'});
    }

    _exportConfigUrl(config, file, areas){
        // classic /export link, or the /transform/export link for an entry served with a published hash
        return exportConfigUrl(this.settings.api, file.path, config, ThemeConfig.previewExportEmbed, areas);
    }

    _initTooltips() {

        // NEW: support dynamic update content on all instances (currently only required for price formula updates - if in the future needed on other use cases we can remove the FORMULA check)
        if(this._tippyInstances && AppExtensions.FORMULA){

            for (let i = 0; i < this._tippyInstances.length; i++) {
                const instance = this._tippyInstances[i];

                let content = instance.reference.getAttribute('data-tippy-content');
                // compare content attribute with current content
                if(instance.props.content !== content){
                    instance.setContent(content);
                }
            }
        }

        // find all tooltips not yet initialized & not empty
        let tooltipElList = this._wrapper.querySelectorAll('[data-tippy-content]:not(.init):not([data-tippy-content=""])');

        // NEW: the personalize group (with its options) is extracted into _popupEl, so scan it too - otherwise its tooltips are never initialized
        if(this._popupEl){
            tooltipElList = [...tooltipElList, ...this._popupEl.querySelectorAll('[data-tippy-content]:not(.init):not([data-tippy-content=""])')];
        }

        if(tooltipElList.length > 0){

            // store all instance references
            if(!this._tippyInstances){
                this._tippyInstances = [];
            }

            for (let i = 0; i < tooltipElList.length; i++) {
                const tooltipEl = tooltipElList[i];

                // NEW: only make interactive if the tooltip contains html
                // INFO: support for links inside help - also fixed wrong position on some themes, might cause other issues: https://atomiks.github.io/tippyjs/v6/faq/#my-tooltip-appears-cut-off-or-is-not-showing-at-all
                // INFO: interactive caused layout shift (eg. in text editor buttons)
                let content = tooltipEl.getAttribute('data-tippy-content');
                let interactive = content.indexOf('<') !== -1 && content.indexOf('>') !== -1; // basic html tag check
                let isMobile = ('ontouchstart' in window); // touch support auto close tooltip

                // NEW: trim content to support excluding tooltip by adding empty optionValue.text " "
                content = content.trim();

                if(content){
                    const toolTipOptions = {
                        allowHTML: true,
                        //zIndex: 99, // default 9999  , 99 to stay below sticky preview: UPDATE: removed again - why should this stay below sticky?? - breaks other z-index
                        maxWidth: 350, // default
                        interactive: interactive,
                        appendTo: ThemeConfig.tooltipBody ? document.body : 'parent', // UPDATE: default append to document to avoid z-index issues: https://atomiks.github.io/tippyjs/v6/all-props/#appendto
                        // DEBUG:
                        // trigger: 'click',  hideOnClick: 'toggle',
                        aria: {
                            content: 'auto',
                            expanded: false, //  not allowed on <div> (removed by customer request)
                        },
                    };

                    // NEW: auto close tooltips on mobile
                    if(isMobile){
                        // NEW: mobile fix to delegate event to allow selection + tooltip: https://github.com/atomiks/tippyjs/releases/tag/v6.3.4
                        toolTipOptions.trigger = 'click';

                        toolTipOptions.onShow = (instance) => {
                            setTimeout(function() { instance.hide() }, (window.clTooltipDuration || 3000)); // default: close after 3 seconds
                        }
                    }

                    this._tippyInstances.push(tippy(tooltipEl, toolTipOptions));
                }

                // mark as initialized to avoid initializing again
                tooltipEl.classList.add('init');
            }
        }

    }

    _initPersonalize(option){

        // add custom controller for custom change logic trigger and validation
        option._controller = {
            onValidate: (option) => {

                if(option.required){

                    // required needs to select the "yes" option
                    if(option.value !== option.values[0].value){

                        // auto enable
                        option.value = option.values[0].value;

                        // render the selected personalize option
                        this.render();

                        // if popup auto select and open personalize
                        if(option.params.popup){
                            // trigger popup
                            this._onPersonalizeChanged(option);
                        } else {  // show optional error message (currently not configurable - not easy dismissable)
                            if(option.params.validationMsg){
                                option.errors.push(option.params.validationMsg);
                            }
                        }

                        return false;
                    }
                }

                return true;
            },
            onChange: (e, option) => {
                // call changed logic directly > render personalize options
                this._optionValueChanged(option);
                // handle popup if enabled
                this._onPersonalizeChanged(option);
            }
        }

        // deep copy to wrap the options
        let optionsPersonalize = [... this.config.options];

        // remove itself
        optionsPersonalize.splice(this.config.options.indexOf(option), 1);

        // remove all other personalize options (causes endless loop)
        optionsPersonalize = optionsPersonalize.filter(o => o.type !== 'personalize')

        // NEW: support to exclude options from personalize
        // INFO: currently params only - no UI in backend - it does not make much sense when used with popup - might be useful with quantity discounts
        let excluded = [];

        // pExclude: comma separated list of option names to exclude from personalize
        if(option.params.pExclude){
            let exclude = option.params.pExclude.split(',');

            optionsPersonalize = optionsPersonalize.filter((o) => {

                if (exclude.indexOf(o.name) !== -1) {
                    // Add the element to the removedElements array
                    excluded.push(o);
                    return false; // exclude from personalize
                }

                return true;
            });
        }

        let optionsGroup = [];
        optionsGroup.push(option);

        // add default value (should be added by backend)
        if(!option.values || option.values.length === 0){
           option.values = [{value: ''}];
        }

        // show when the first value is selected
        let personalizeCondition = { data: 'options', field: option.name, operator: 'equals', value: option.values[0].value };

        optionsGroup.push({
            "name": "group_personalize",
            "type": "group",
            "value": "",
            "required": false,
            "conditions": [ personalizeCondition ],
            "params": {"collapseOpen": false, "collapseLabel": "", "collapse": false},
            "options": optionsPersonalize,
            "id": "option_group_personalize",
            "visible": false
        });

        // editorMode: disable popup flag (only on client side - not saved on server)
        if(this.settings.editorMode && option.params.popup){
            option.params.popup = false;
        }

        if(option.params.popup){
            // IMPORTANT: force product form option linking (popup options are always outside of the form)
            this._productFormLinkForce = true;
        }

        // preview add personalize condition on layers that are connected to an option
        // INFO: alternative would be to destroy the preview when personalize option is disabled - but this would require a major refactor - and with app gallery not possible
        if(this.config.preview){

            // popup avoid initializing preview until popup is opened
            if(option.params.popup){
                this.config.preview = false;
            }

            const addCondition = (layers, condition) => {
                for (let i = 0; i < layers.length; i++) {
                    const layer = layers[i];
                    // check if layer references an option
                    if(layer.params.option){
                        layer.conditions.unshift(condition);
                    }
                }
            }
            addCondition(this.config.previewConfig.layers, personalizeCondition);

            if(this.config.previewConfig.gallery){
                for (let i = 0; i < this.config.previewConfig.views.length; i++) {
                    const view = this.config.previewConfig.views[i];
                    addCondition(view.layers, personalizeCondition);
                }
            }
        }

        // update main reference
        this.config.options.length = 0;

        if(excluded.length){
            this.config.options.push(...excluded);
        }

        this.config.options.push(...optionsGroup);

        // store reference (currently only used to auto open popup on restore from cart)
        this._personalizeOption = option;

        // return all the options that are wrapped into the personalize group
        return optionsPersonalize;
    }

    _initTabs(option){

        // Check if we're inside a personalize structure
        let isInsidePersonalize = this.config.options.some(o =>
            o.type === 'group' && o.name === 'group_personalize'
        );

        let optionsTabs;
        let targetOptions;

        if(isInsidePersonalize) {
            // Find the personalize group and work with its nested options
            let personalizeGroup = this.config.options.find(o =>
                o.type === 'group' && o.name === 'group_personalize'
            );

            targetOptions = personalizeGroup.options;
            optionsTabs = [... targetOptions];

            // Remove tabs from the nested array
            let tabsIndex = targetOptions.indexOf(option);
            if(tabsIndex !== -1) {
                optionsTabs.splice(tabsIndex, 1);
            }

            // Remove all other tabs options (prevent nesting conflicts)
            optionsTabs = optionsTabs.filter(o => o.type !== 'tabs');

            // Wrap options into tabs
            option.options = optionsTabs;

            // Update the personalize group's options (not root config.options)
            personalizeGroup.options = [option];

        } else {
            // Original logic for root-level tabs
            optionsTabs = [... this.config.options];

            // Remove itself
            optionsTabs.splice(this.config.options.indexOf(option), 1);

            // Remove all other tabs options (prevent nesting conflicts)
            optionsTabs = optionsTabs.filter(o => o.type !== 'tabs');

            // Wrap options into the tabs option
            option.options = optionsTabs;

            // Update main reference
            this.config.options.length = 0;

            this.config.options.push(option);
        }

        // Return all the options that are wrapped into tabs
        return optionsTabs;
    }

    _onPersonalizeChanged(option){

        if(option.params.popup){

            // first value is yes
            if(option.value === option.values[0].value){

                // enable preview
                this.config.preview = true;

                // extract hardcoded group element into popup
                if(!this._personalizeGroupEl){
                    this._personalizeGroupEl = this._wrapper.querySelector(`[data-option="group_personalize"]`);
                    // DEPRECATED: remove from dom to avoid showing while popup is still loading
                    // INFO: this causes init of async elements (e.g choices dropdown) to break - popup render moves the node automatically
                    //this._personalizeGroupEl.parentNode.removeChild(this._personalizeGroupEl);
                    this._personalizeGroupEl.style.display = 'none';  // we hide the group instead (is removed after first popup render)
                }

                let popupReady = Promise.resolve(true);

                if(!ResourceLoader.isDefined('popup')){
                    // resources loaded async
                    let resources = [
                        this.settings.assets + '/static/lib/ldcover/ldcv.full.min.css',
                        this.settings.assets + '/static/lib/ldcover/ldcv.min.js'
                    ];

                    /* load resources if not already ( this.ready can be checked from outside with a promise.then ) */
                    popupReady = ResourceLoader.loadOnce(resources, 'popup',  {async: false });
                }

                popupReady.then(() => {

                    // only init once - then toggle
                    if(!this._popupEl){

                        // create wrapper to render into
                        this._popupEl = document.createElement('div');

                        // price shown on popup
                        this._popupPriceEl = document.createElement('span');

                        // add price el to price list to update when changed
                        if(this._priceElList){
                            // existing list
                            if(Util.isNodeList(this._priceElList)){
                                // convert node list to array to be able to add to it
                                this._priceElList = Array.prototype.slice.call(this._priceElList);
                            }
                            this._priceElList.push(this._popupPriceEl)
                        } else {
                            this._priceElList = [ this._popupPriceEl ];
                        }

                        // initial render
                        this._renderPopup(option);

                        // reset style (only used for improved UX to avoid flickering options outside of popup on screen)
                        this._personalizeGroupEl.style.display = '';

                        document.body.appendChild(this._popupEl);

                        this._popup = new ldCover({ root: this._popupEl.querySelector('.ldcv'), lock: false }); // pass the reference to the popup root

                        this._popup.on('toggle.on', () => {
                            // init preview
                            if(!this._preview){

                                // hardcoded mobile set the preview height at max 1/4 of the screen
                                if(window.innerWidth <= 768){
                                    this.settings.maxHeightPreview = Math.floor(window.innerHeight / 4);

                                    // NEW: hacky but works - use stickyPreviewMobileSize if not default 25% to control height (sticky preview can also be disabled)
                                    if(this.settings.stickyPreviewMobileSize !== 25){
                                        this.settings.maxHeightPreview = Math.floor(window.innerHeight * (this.settings.stickyPreviewMobileSize / 100));
                                    }

                                } else {
                                    // gallery needs more space for thumbnails below
                                    let isGalleryBottom = this.config.previewConfig.gallery && this.config.previewConfig.galleryConfig.layout === 'bottom';
                                    // max height in popup
                                    this.settings.maxHeightPreview = isGalleryBottom ? 550 : 660;
                                }

                                this._initPreview();

                            } else {
                                this._renderPreview();
                            }

                            this.render();

                        });

                    } else {
                        // render again (to show options again)
                        this._renderPopup(option);
                    }

                    // popup returns promise when closed
                    this._popup.get().then((result) => {

                        // just close without resetting personalization (custom add to cart click)
                        if(this._popupClose){
                            delete this._popupClose; // reset flag
                            return;
                        }

                        // currently always reset to no personalization
                        option.value = option.values[1].value;

                        this.render();
                        this._renderPreview(); // remove personalization inputs on preview (required to avoid generating wrong exports)

                        // render popup again (only used to hide options - because lit html template top level if is not working when we extracted the option group)
                        this._renderPopup(option);
                    });

                    this._popup.toggle();
                })
            } else {
                if(this._popup){
                    // return value / hide ( triggers on close promise )
                    this._popup.set(false, true);
                }
            }
        }


    }

    _renderPopup(option) {

        let popupConfig = {
            id: this.config.uuid,
            product: this.settings.product,
            productFormLink: this._productFormLink, // quantity link to product form
            className: 'fade' + (option.params.popupFull ? ' full' : ''),
            option: option,
            visible: this.config.options[1].visible // options personalize group visible state
        };

        // live preview container
        let previewEl = this.settings.selectorPreview;

        // no live preview enabled? select current product image
        if (!Util.isElement(previewEl)) {
            previewEl = document.createElement('img');
            previewEl.className = 'cl-po--gallery-img';

            if (this._variantId) {
                for (let i = 0; i < this.settings.product.variants.length; i++) {
                    const variant = this.settings.product.variants[i];
                    if (variant.id.toString() === this._variantId.toString()) {
                        if (variant.featured_image && variant.featured_image.src) {
                            previewEl.src = variant.featured_image.src;
                        }
                        break;
                    }
                }
            }

            if (!previewEl.src) {
                previewEl.src = this.settings.product.featured_image;
            }
        }


        render(productOptionsPopupTemplate(previewEl, this._personalizeGroupEl, this._popupPriceEl, popupConfig, (e) => {

            this._productFormSubmit = false;

            // NEW: custom selector to add to cart via button to support ajax carts with popup
            let cartBtnEl = document.querySelector('[data-live-cart-add-popup]');
            if (cartBtnEl) {

                // we have to close the popup when the options have been submitted (only triggered when options are valid)
                window.addEventListener('options:submit', (e) => {
                    this._popupClose = true; // set flag to avoid reset after close
                    this._popup.set('close', true);  // close without resetting the options
                }, {once: true}); // test event is added many times if options invalid - but no problem the close is only executed once

                // trigger the click on the cart button from the theme IMPORTANT: the theme has to support getting all the options from the popup (formLink - e.g Dawn based themes support it)
                cartBtnEl.click();
            } else {

                // NEW: single global cart update handler
                let onSuccess = undefined;
                if (typeof window.clOnCartUpdate === 'function') {
                    // we have to warp the handler to manually close the popup
                    onSuccess = () => {
                        this._popup.set('close', true);  // close with reset (add to cart already done)
                        // trigger handler
                        window.clOnCartUpdate();
                    }
                }

                // validation and add-on logic > then full add to cart
                this._handleFormSubmit(new CustomEvent('cloudlift.cart', {detail: {onSuccess: onSuccess}, cancelable: true, bubbles: false}));
            }

        }, (e) => {
            this._popup.set('close', true);
        }), this._popupEl);
    }

    _destroyTooltips(){
        if(this._tippyInstances != null){
            for (let i = 0; i < this._tippyInstances.length; i++) {
                const tippyInstance = this._tippyInstances[i];
                tippyInstance.reference.classList.remove('init');
                tippyInstance.destroy();
            }
        }
    }

    _restoreCartItem() {
        let itemKey = Util.getQueryParam(window.location.href, 'item');
        // find the item in the cart
        return this.shopifyCart.getCart(true).then(async (cart) => {
            for (let i = 0; i < cart.items.length; i++) {
                const item = cart.items[i];
                if(item.key === itemKey && item.properties){
                    log.debug('restoring cart item values: ', item.properties)
                    this.setValues(item.properties);
                    this.render();

                    // NEW: view state restore
                    await this._queuePreviewStates(item.properties);

                    this._renderPreview();

                    // quantity update
                    if(item.quantity > 1){
                        // update html element (not supported on themes with very custom quantity selectors)
                        let quantityEl = this.shopifyStore.findProductFormQuantityEl((this._productFormParentEl || this._productForm));
                        if(quantityEl) {
                            quantityEl.value = item.quantity;
                            // trigger change event (quantity option sync)
                            quantityEl.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                    }

                    // flag to ask if update/replace
                    if(ThemeConfig.productItemModal){
                        if (typeof HTMLDialogElement !== 'function') {
                            this._dialogPolyfill = ResourceLoader.loadOnce([
                                this.settings.assets + '/static/lib/dialog/dialog-polyfill.min.js',
                                this.settings.assets + '/static/lib/dialog/dialog-polyfill.min.css',
                            ], 'dialog',  {async: false });
                        }
                    }
                    // store key > used for cart update
                    this._itemKey = itemKey;

                    // personalize restore auto popup
                    if(this._personalizeOption){
                        this._onPersonalizeChanged(this._personalizeOption);
                    }

                    break;
                }
            }
        })
    }

    // NEW: ?cl_order=<token>&cl_line=<line id> ( buy again per line on the product page ) > the ordered line opens like an entry of the "My orders" tab:
    // values checked against the live options, skipped ones reported, files restored, price recomputed - no configuration of the shopper ( order: true )
    // only the instance of the line's configuration applies it, a failure stays silent - the parameters are dropped either way
    async _openOrderLineFromUrl() {
        const line = SavedConfigs.urlParam(Order.LINE_PARAM);
        try {
            const order = await Order.fetchOrder({ api: this.settings.api, shop: this.settings.queryParams.shop, app: 'options' }, SavedConfigs.urlParam(Order.RESTORE_PARAM));
            const item = (order && Array.isArray(order.items) ? order.items : []).find((entry) => String(entry.lineId) === line);
            const properties = (item && item.properties) || {};

            if (properties[CartItemProperties.OPTIONS] === this.config.uuid) {
                await this._configs.open({
                    order: true,
                    configUuid: this.config.uuid,
                    properties: this._configs.owned(properties),
                    variantId: item.id
                });
            }
        } catch (error) {
            log.warn('[live-options] order line not opened', error);
        }

        const url = new URL(window.location.href);
        [Order.RESTORE_PARAM, Order.LINE_PARAM, Order.SOURCE_PARAM].forEach((name) => url.searchParams.delete(name));
        SavedConfigs.replaceUrl(url.toString());
    }

    // NEW: options scope (default all) so merged configs can prefill only their new options
    //      doRender=false lets the merge path skip the render here and use its own (currency-aware) render
    _prefillFromUrl(options = this.config.options, doRender = true) {
        // get all query parameters from the URL
        let allParams = Util.getQueryParams(window.location.href);

        // create values object, excluding '_f' and 'item' parameters
        let values = {};
        for(const param in allParams){
            if(allParams.hasOwnProperty(param) && param !== '_f' && param !== 'item'){
                // decode name (strangely umlauts names are not decoded automatically)
                if(param.includes('%')){
                    values[decodeURIComponent(param)] = allParams[param];
                } else {
                    values[param] = allParams[param];
                }
            }
        }

        // only proceed if there are values to prefill
        if(Object.keys(values).length > 0){
            log.debug('prefilling options from URL: ', values);
            this.setValues(values, options);
            if(doRender){
                this.render();
                this._renderPreview();
            }
        }
    }

    // generates a shareable URL with current option values as query parameters
    _generatePermalink() {
        // get current option values
        let values = this.getValues();

        // build base URL from current page
        let url = window.location.origin + window.location.pathname;

        let params = [];

        // add each option as a query parameter
        for (const name in values) {
            if (values.hasOwnProperty(name) && values[name]) {
                let value = values[name];
                // reverse cors (required to support restore)
                if(value.includes('googleapis.com')){
                    // reverse of upload.js _corsUrl
                    value = value.replace('https://storage.googleapis.com/cloudlift-app-cloud-dev-upload', 'https://upload.dev.cloudlift.app/s/dev');
                    value = value.replace('https://storage.googleapis.com/cloudlift-app-cloud-prod-upload', 'https://upload.cloudlift.app/s');
                }
                // encode both name and value to handle special characters
                params.push(encodeURIComponent(name) + '=' + encodeURIComponent(value));
            }
        }

        // combine URL with parameters
        if (params.length) {
            params.unshift('_f'); // add trigger param

            // include variant id
            if(this._variantId){
                params.unshift('variant=' + this._variantId);
            }

            url += '?' + params.join('&');
        }

        return url;
    }

    _lookupCartQuantity(variantId){
        // HACKY: we wrap the lookup into another promise with a slight delay to get the cached cart on initial page load (otherwise each option triggers a cart fetch as the first lookup is not yet done)
        // could be solved on the shopifyCart.getCart with promise caching - but there is already custom .cache reset logic that we don't want to change
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                this.shopifyCart.getCart(true).then((cart) => {
                    let quantity = 0;
                    for (let i = 0; i < cart.items.length; i++) {
                        const item = cart.items[i];
                        // check variant match
                        if(item.variant_id.toString() === variantId.toString()){
                            quantity += item.quantity;
                        }
                        // NEW: bundle contents
                        if(item.properties.hasOwnProperty(CartItemProperties.BUNDLE)){
                            let bundleItems = JSON.parse(item.properties[CartItemProperties.BUNDLE]);
                            if(bundleItems && bundleItems.length){
                                for (let j = 0; j < bundleItems.length; j++) {
                                    const bundleItem = bundleItems[j];
                                    if(bundleItem.id.toString() === variantId.toString()){
                                        quantity += bundleItem.quantity;
                                    }
                                }
                            }
                        }
                    }
                    return resolve(quantity);
                });
            }, 500);
        });
    }

    _hasOptionsValues(){
        let values = this.getValues();
        // filter to only check visible values
        for(const key in this.visible){
            if(!this.visible[key]){
                delete values[key];
            }
        }
        //  check if at least one option has a value
        return Object.values(values).some(value => value !== '');
    }

    _disableEmptyOptions() {
        let values = this.getValues();

        let restoreEl = [];

        for(const key in values){
            if(!values[key]){
                let excludeElList = document.querySelectorAll(`[name="properties[${key}]"]`);
                if(excludeElList.length){
                    for (let i = 0; i < excludeElList.length; i++) {
                        const el = excludeElList[i];
                        if(!el.disabled){
                            // disable excludes the element from the form submit
                            el.disabled = true;
                            restoreEl.push(el);
                        }
                    }
                }
            }
        }

        if(restoreEl.length){
            // restore after submit event
            this.element.addEventListener('options:submit', (e) => {
                restoreEl.forEach(el => {
                    el.disabled = false;
                });
            }, { once: true });
        }
    }

    // deduct current variant price from options price
    _dVP(priceValue, baseCurrency = true){
        let variant = this._getVariantById(this._variantId);
        if(variant?.price){ // micros conversion

            let variantPrice = (variant.price / 100);

            // variantPrice is always in current shop presentment currency (not base)
            if(baseCurrency && ThemeCurrency.currencyConvert && ThemeCurrency.currencyFactor !== 1){
                variantPrice *= (1 / ThemeCurrency.currencyFactor); // IMPORTANT: inverse factor
                variantPrice = Math.round(variantPrice * 100) / 100; // round to 2 decimals
            }

            priceValue = priceValue - variantPrice;
            // NEW: allow negative variant deduction (only useful when price formula calculates a quantity based price and then it's multiplied by shopify cart)
            if(!ThemeCurrency.checkout.negative){
                priceValue = Math.max(priceValue, 0); // avoid going negative
            }
        }
        return priceValue;
    }

    // price proxy readonly to avoid debugger modification (not secure, it's always possible to modify the value before it's passed into this function)
    _pp(priceObj){
        return new Proxy(priceObj, {
            set(target, property, value) {
                // ignore setter call
                return true;
            }
        });
    }

    // price key (function name to make harder to debug)
    _pk(optionPrice, key){

        // key is only added to make the hash unique for each store (in the future key should be kept private for truly secure price hash calculation)
        const message = `${key}:${optionPrice}`;

        // src: https://stackoverflow.com/a/8831937
        let hash = 0;
        for (let i = 0, len = message.length; i < len; i++) {
            let chr = message.charCodeAt(i);
            hash = (hash << 5) - hash + chr;
            //hash |= 0; // Convert to 32bit integer
            hash >>>= 0; // Unsigned int (>>>)
        }

        return hash;

        /* crypto NOT supported in shopify functions:
           https://shopify.dev/docs/apps/functions/language-support#choosing-a-language (in RUST maybe possible)
           https://github.com/Shopify/function-examples/discussions/373

        const enc = new TextEncoder("utf-8");
        const algorithm = { name: "HMAC", hash: "SHA-256" };
        const key = await crypto.subtle.importKey(
            "raw",
            enc.encode(secret),
            algorithm,
            false, ["sign", "verify"]
        );
        const hashBuffer = await crypto.subtle.sign(
            algorithm.name,
            key,
            enc.encode(message)
        );
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(
            b => b.toString(16).padStart(2, '0')
        ).join('');
        return hashHex;
         */
    }
}

export default ProductOptions;
